import { domainMaxBounds } from '../domain/limits'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import { ARROW_COLOR, ARROW_ICON_PX, ARROW_STROKE_PX, waveArrowBuckets } from '../models/arrows'
import {
  TRACK_ARROW_PX,
  TRACK_ARROW_SPACING_PX,
  TRACK_COLOR,
  TRACK_CONTEXT_FADE,
  TRACK_OPACITY_FRESH,
  TRACK_OPACITY_OLD,
  TRACK_WIDTH_ACCENT,
  TRACK_WIDTH_CONTEXT,
} from '../models/trackStyle'
import {
  driftCircleColor,
  driftCircleOpacity,
  driftCircleRadius,
  driftCircleStrokeColor,
  driftCircleStrokeWidth,
  driftPointFilter,
} from '../models/driftMarkerStyle'
import {
  GAP_DASHARRAY,
  operationColor,
  operationColorExpr,
  operationFilter,
  operationOpacityExpr,
} from './vesselOps'
import {
  vesselColor,
  VESSEL_ICON_PX,
  VESSEL_IMAGE,
  drawVesselImage,
  vesselIconImage,
  vesselIconRotate,
} from './vesselMarkerStyle'
import {
  MARK_LABEL_MIN_ZOOM,
  markCirclePaint,
  markLabelLayout,
  markRankImageId,
  MARK_RANKS,
} from '../models/nnMarksLayer'
import { getLang, t } from '../i18n'
import type { LayerPalette } from '../api'
import { useLayerPalette } from '../maps/useLayerPalette'
import { baseStyle } from './baseStyle'
import { releaseActiveMap, setActiveMap } from './activeMap'
import { registerBasemapMap } from './basemapRefresh'
import { BASE_SOURCE_ID } from './baseStyle'
import { basemapDown, basemapFailure, basemapFailureMsg, type BasemapFailure } from './basemapHealth'
import { canonicalBBox } from './bbox'
import { setViewBBox } from '../species/viewportStore'
import { createStyleGate } from './styleGate'
import { MapGraticule } from './MapGraticule'
import { CoordReadout } from './CoordReadout'
import { OVERLAY_PREFIX, addUnderBoundaries, firstOverlayLayerId, isobathBeforeId } from './layerOrder'
import {
  forecastConfidenceFilter,
  forecastLinePaint,
  forecastMarkPaint,
} from './vesselForecastStyle'
import {
  ISOTHERM_CASING_COLOR,
  ISOTHERM_CASING_OPACITY,
  ISO_LABEL_MIN_ZOOM,
  ISO_LABEL_PREFIX,
  isothermLineColor,
  isothermCasingWidthExpr,
  isothermLabelImageId,
  isothermLabelSourceId,
  isothermLayerId,
  isothermLineWidthExpr,
  isothermSourceId,
} from './isotherms'
import { isothermLabelText, type IsoLabelProps } from './isothermFamily'
import { upwLabelText, type UpwIsoLabelProps } from './upwellingIso'
import {
  UPW_CASING_COLOR, UPW_CASING_OPACITY, UPW_DASH, UPW_ZERO_WIDTH,
  upwIsoLabelSourceId, upwIsoLayerId, upwIsoSourceId, upwWidthExpr,
} from './upwellingIsoLayers'

/** Приставка образов подписи изолинии механизма. */
const UPW_LABEL_PREFIX = 'upw-lbl-'
import {
  FRONT_CASING_COLOR,
  FRONT_CASING_OPACITY,
  frontLineColor,
  FRONT_LABEL_BG,
  frontCasingWidthExpr,
  frontLabelImageId,
  frontLayerId,
  frontLineWidthExpr,
  frontSourceId,
} from './frontsStyle'
import {
  waveIsoLayerId,
  waveIsoSourceId,
  waveIsoLabelSourceId,
  waveIsoLabelImageId,
  waveLabelText,
  WAVE_ISO_LABEL_PREFIX,
  type WaveLabelProps,
} from './waveContours'
import { rasterMaxZoom, rasterSourceSpec } from './rasterZoom'
import { earthquakeColor } from './layerOrder'
import { earthquakeZones, epicenterRadius, zoneOpacity } from './earthquakeZones'
import {
  hsiArrowLayout,
  hsiColorExpr,
  hsiDriftLayerId,
  hsiDriftLayerOrder,
  hsiDriftSourceId,
  hsiHoverFilter,
  hsiHoverPaint,
  hsiLineWidthExpr,
  hsiOriginPaint,
  hsiArrowColor,
  type HsiDriftPart,
  HSI_ARROW_IMAGE_ID,
  HSI_ARROW_PX,
  HSI_DRIFT_PREFIX,
  HSI_HOVER_HIT_PX,
} from './hsiDrift'
import { useHsiHighlight } from './driftHighlight'
import {
  hsiCometLayerId,
  hsiCometSourceId,
  hsiCometWidthExpr,
  HSI_COMET_PREFIX,
  TIP_FILTER,
} from './hsiComet'
import { haloCollection } from '../models/driftHalo'
import type { DriftCollection } from '../api'
import { BasemapAttribution } from './attribution/BasemapAttribution'
import { ISOBATH_TOOL_KEY } from './isobathStep'
import {
  ISOBATH_LABEL_MIN_ZOOM,
  isobathDepthsOf,
  isobathRampCasingWidthExpr,
  isobathRampColorExpr,
  isobathRampIconExpr,
  isobathRampLabelImageId,
  isobathRampWidthExpr,
  isobathLabelLayerId,
  isobathRampLayerId,
  isobathLayerId,
  isobathStyle,
  isobathWidthExpr,
} from './isobaths'
import { EEZ_DASH_GROUPS, eezDashLayerId } from './eez'
import { DISTRICT_LAYER_IDS, applyDistrictLayers, removeDistrictLayers } from './districtsLayer'
import { applySubstrateLayers, removeSubstrateLayers } from './substrateLayer'
import { applyNavwarnLayers, removeNavwarnLayers } from './navwarnLayer'
import { applyBanLayers, removeBanLayers } from './bansLayer'
import { applyEezLayers, removeEezLayers } from './eezLayer'

const ARROW_IMAGE_ID = 'current-arrow'

/** Держать стрелки течений ПОВЕРХ температурных линий того же ключа: белые
 *  подложки изотерм/фронтов иначе стирали бы стрелки на пересечениях. Вызываем
 *  при добавлении температурных слоёв (на слоях без стрелок — no-op). */
function raiseArrowsToTop(map: maplibregl.Map, key: string) {
  const arrowsLyr = OVERLAY_PREFIX + 'arrows-' + key + '-layer'
  if (map.getLayer(arrowsLyr)) map.moveLayer(arrowsLyr)
}

/** Иконка стрелки — рисуется в canvas один раз на карту.
 *
 *  Контурная, а не залитая: чёрный волосок читается поверх любой части шкалы
 *  течений и не спорит с ней цветом — модуль скорости кодирует фон, стрелка
 *  отвечает только за направление. Остриё смотрит ВВЕРХ (на север) —
 *  icon-rotate доворачивает его по курсу. */
/** Рисует один образ стрелки заданного цвета. Тот же глиф («птичка»), одна длина —
 *  меняется только цвет (для оверлея «Волнение» готовим рампу таких образов). */
function drawArrowImage(map: maplibregl.Map, id: string, color: string) {
  if (map.hasImage(id)) return
  // Рисуем в 3× и отдаём с pixelRatio: 3. Не 2: на предельных зумах стрелка
  // растягивается до 1.6× (см. ARROW_MAX_SCALE), и запаса 2× не хватает —
  // тонкая линия расплывается в серую кляксу.
  const ratio = 3
  const s = ARROW_ICON_PX * ratio
  const canvas = document.createElement('canvas')
  canvas.width = s
  canvas.height = s
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const mid = s / 2
  const pad = ARROW_STROKE_PX * ratio // поле под обводку, иначе остриё срежется краем
  const head = s * 0.34
  ctx.strokeStyle = color
  ctx.lineWidth = ARROW_STROKE_PX * ratio
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.moveTo(mid, s - pad)                        // древко
  ctx.lineTo(mid, pad)
  ctx.moveTo(mid - head * 0.5, pad + head * 0.62) // наконечник «птичкой»
  ctx.lineTo(mid, pad)
  ctx.lineTo(mid + head * 0.5, pad + head * 0.62)
  ctx.stroke()
  const img = ctx.getImageData(0, 0, s, s)
  map.addImage(id, { width: s, height: s, data: img.data }, { pixelRatio: ratio })
}

function ensureArrowImage(map: maplibregl.Map) {
  drawArrowImage(map, ARROW_IMAGE_ID, ARROW_COLOR)
}

/** Рампа цветных образов стрелки для оверлея «Волнение» (ink → красный). */
function ensureWaveArrowImages(map: maplibregl.Map) {
  for (const b of waveArrowBuckets()) drawArrowImage(map, b.id, b.color)
}

/** icon-image для слоя стрелок: базовый ink-образ или step по высоте волны
 *  (штиль/нет данных → ink, выше — краснеющие образы). */
function arrowIconImage(colorByWave: boolean): maplibregl.DataDrivenPropertyValueSpecification<string> {
  if (!colorByWave) return ARROW_IMAGE_ID
  const expr: unknown[] = ['step', ['coalesce', ['get', 'wave_hs'], -1], ARROW_IMAGE_ID]
  for (const b of waveArrowBuckets()) expr.push(b.hs, b.id)
  return expr as unknown as maplibregl.DataDrivenPropertyValueSpecification<string>
}

/** Четыре образа судна: {треугольник|точка} × {свежий|устаревший}.
 *  Готовим канвасом, как стрелки течений: цвет запечён в образ, потому что
 *  icon-color работает только с SDF, а SDF из сплошной фигуры мылит края. */
function ensureVesselImages(map: maplibregl.Map) {
  const ratio = 3
  const s = VESSEL_ICON_PX * ratio
  const specs = [
    { id: VESSEL_IMAGE.triFresh, color: vesselColor().fresh, shape: 'triangle' as const },
    { id: VESSEL_IMAGE.triStale, color: vesselColor().stale, shape: 'triangle' as const },
    { id: VESSEL_IMAGE.hollowFresh, color: vesselColor().fresh, shape: 'hollow' as const },
    { id: VESSEL_IMAGE.hollowStale, color: vesselColor().stale, shape: 'hollow' as const },
  ]
  for (const spec of specs) {
    if (map.hasImage(spec.id)) continue
    const canvas = document.createElement('canvas')
    canvas.width = s
    canvas.height = s
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    drawVesselImage(ctx, s, spec.color, spec.shape)
    const img = ctx.getImageData(0, 0, s, s)
    map.addImage(spec.id, { width: s, height: s, data: img.data }, { pixelRatio: ratio })
  }
}

const TRACK_ARROW_IMAGE_ID = 'drift-track-arrow'

/** Стрелка направления ВДОЛЬ ТРЕКА — «птичка» остриём ВПРАВО (+x).
 *
 *  Вправо, а не вверх: при `symbol-placement: 'line'` MapLibre доворачивает
 *  иконку по касательной к линии, совмещая её ось x с направлением трека.
 *
 *  Одним цветом с линией, без обводки. */
function ensureTrackArrowImage(map: maplibregl.Map) {
  if (map.hasImage(TRACK_ARROW_IMAGE_ID)) return
  const ratio = 3
  const s = TRACK_ARROW_PX * ratio
  const canvas = document.createElement('canvas')
  canvas.width = s
  canvas.height = s
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const mid = s / 2
  const pad = s * 0.18
  ctx.strokeStyle = TRACK_COLOR
  ctx.lineWidth = 1.8 * ratio
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.moveTo(pad, pad)
  ctx.lineTo(s - pad, mid)   // остриё вправо
  ctx.lineTo(pad, s - pad)
  ctx.stroke()
  const img = ctx.getImageData(0, 0, s, s)
  map.addImage(TRACK_ARROW_IMAGE_ID, { width: s, height: s, data: img.data }, { pixelRatio: ratio })
}

/** Шеврон направления ВДОЛЬ УСИКА — остриём ВПРАВО (+x), как стрелка трека.
 *
 *  Белый и тоньше трековой стрелки: усиков в кадре сотни, и жирный шеврон
 *  забивал бы саму окраску по индексу. */
function ensureHsiArrowImage(map: maplibregl.Map) {
  if (map.hasImage(HSI_ARROW_IMAGE_ID)) return
  const ratio = 3
  const s = HSI_ARROW_PX * ratio
  const canvas = document.createElement('canvas')
  canvas.width = s
  canvas.height = s
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const mid = s / 2
  const pad = s * 0.22
  ctx.strokeStyle = hsiArrowColor()
  ctx.lineWidth = 1.3 * ratio
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.moveTo(pad, pad)
  ctx.lineTo(s - pad, mid) // остриё вправо — туда, куда идёт ломаная
  ctx.lineTo(pad, s - pad)
  ctx.stroke()
  const img = ctx.getImageData(0, 0, s, s)
  map.addImage(HSI_ARROW_IMAGE_ID, { width: s, height: s, data: img.data }, { pixelRatio: ratio })
}

/** Номера мест нейрокарты — картинками: глифов у карты нет (см. ниже). */
function ensureNnRankImages(map: maplibregl.Map) {
  const ratio = 3
  for (let rank = 1; rank <= MARK_RANKS; rank++) {
    const id = markRankImageId(rank)
    if (map.hasImage(id)) continue
    const s = 14 * ratio
    const canvas = document.createElement('canvas')
    canvas.width = s
    canvas.height = s
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.font = `700 ${9 * ratio}px "IBM Plex Sans", system-ui, sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = '#ffffff'
    ctx.fillText(String(rank), s / 2, s / 2 + ratio * 0.5)
    const img = ctx.getImageData(0, 0, s, s)
    map.addImage(id, { width: s, height: s, data: img.data }, { pixelRatio: ratio })
  }
}

/** Подпись глубины как КАРТИНКА, а не текстовый слой.
 *
 *  Текст в MapLibre требует сервера глифов (`glyphs` в стиле), которого у карты
 *  кабинета нет и который ради пяти подписей пришлось бы поднимать вместе с
 *  кириллическими PBF. Пять маленьких canvas-иконок дают тот же результат без
 *  единого нового запроса. Цвет — цвет своей изобаты, подложка светлая: подпись
 *  должна читаться и над тёмным полем индекса, и над сушей. */
/** id картинки-подписи карты апвеллинга (цветом своей линии). У раздела с шагом
 *  имя своё — `isobathRampLabelImageId`: подписи там тёмные, а кэш `hasImage`
 *  общий и различает картинки только по имени. */
function isobathLabelImageId(depth: number): string {
  return `isobath-label-${Math.abs(depth)}`
}

/** Чернила подписи глубины. Цветом линии писать нельзя: на мелководье шкала
 *  почти белая, и «10 м» на белой плашке исчезает. Глубину называет сам текст,
 *  а цвет несёт линия рядом — подписи хватает одного тёмного тона. */
const ISOBATH_LABEL_INK = '#0b2743'

function ensureIsobathLabelImage(map: maplibregl.Map, depth: number, ink?: string) {
  const id = ink ? isobathRampLabelImageId(depth) : isobathLabelImageId(depth)
  if (map.hasImage(id)) return
  const ratio = 3
  const text = `${Math.abs(depth)} ${t('units.m')}`
  const font = `600 ${9 * ratio}px "IBM Plex Mono", ui-monospace, monospace`
  const probe = document.createElement('canvas').getContext('2d')
  if (!probe) return
  probe.font = font
  const padX = 3 * ratio
  const w = Math.ceil(probe.measureText(text).width) + padX * 2
  const h = 13 * ratio
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.fillStyle = 'rgba(255,255,255,0.82)'
  // roundRect есть не везде (Safari < 16.4) — там просто прямая плашка.
  if (typeof ctx.roundRect === 'function') {
    ctx.beginPath()
    ctx.roundRect(0, 0, w, h, 2 * ratio)
    ctx.fill()
  } else {
    ctx.fillRect(0, 0, w, h)
  }
  ctx.font = font
  ctx.fillStyle = ink ?? isobathStyle(depth).color
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, w / 2, h / 2 + 0.5 * ratio)
  map.addImage(id, ctx.getImageData(0, 0, w, h), { pixelRatio: ratio })
}

/** Слои одной коллекции изобат: линия + подпись на КАЖДУЮ пришедшую глубину.
 *
 *  Слой на глубину, а не один общий: `line-dasharray` в MapLibre не принимает
 *  data-driven выражений, начертанием по свойству фичи управлять нельзя. Отсюда
 *  и фильтр `depth == …` у каждого слоя — и отсюда же требование строить слои
 *  ровно по тем глубинам, что реально лежат в данных.
 *
 *  `depths` приходит от мелкой к глубокой (`isobathDepthsOf`), и порядок
 *  добавления сохраняет наложение: заметные 500/1000 м ложатся поверх мелких. */
function addIsobathLayers(
  map: maplibregl.Map,
  key: string,
  src: string,
  depths: number[],
  bathy: LayerPalette | undefined,
) {
  // Раздел с шагом — по шкале глубин, тремя слоями. Карта апвеллинга остаётся
  // ровно прежней: её пятёрка глубин со штрихами повторяет телеграм-картинку,
  // и «та же карта» обязана оставаться той же.
  if (key === ISOBATH_TOOL_KEY) {
    addRampIsobathLayers(map, key, src, depths, bathy)
    return
  }
  for (const depth of depths) {
    const style = isobathStyle(depth)
    const paint: maplibregl.LineLayerSpecification['paint'] = {
      'line-color': style.color,
      'line-width': isobathWidthExpr(style.width) as maplibregl.ExpressionSpecification,
      // Ниже единицы линии дна начинают спорить с самим полем индекса.
      'line-opacity': 0.9,
    }
    if (style.dash) paint['line-dasharray'] = style.dash
    map.addLayer(
      {
        id: isobathLayerId(key, depth),
        type: 'line',
        source: src,
        filter: ['==', ['get', 'depth'], depth],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint,
      },
      isobathBeforeId((map.getStyle().layers ?? []).map(l => l.id)),
    )
    // Подпись глубины — по центру каждого куска линии, как `clabel` на
    // телеграм-карте. `line-center`, а не `line`: одна подпись на кусок,
    // иначе на длинной изобате их выстраивается лента.
    // Подпись — украшение поверх линии: её падение не должно уносить с
    // собой остальные глубины (цикл прервался бы на первой же ошибке).
    try {
      ensureIsobathLabelImage(map, depth)
    } catch {
      continue
    }
    map.addLayer(
      {
        id: isobathLabelLayerId(key, depth),
        type: 'symbol',
        source: src,
        filter: ['==', ['get', 'depth'], depth],
        minzoom: ISOBATH_LABEL_MIN_ZOOM,
        layout: {
          'icon-image': isobathLabelImageId(depth),
          'symbol-placement': 'line-center',
          // Пересекающиеся подписи MapLibre выбрасывает сам — это и нужно:
          // лучше меньше подписей, чем нечитаемая каша.
          'icon-allow-overlap': false,
          'icon-rotation-alignment': 'viewport',
          'icon-padding': 4,
        },
      },
      isobathBeforeId((map.getStyle().layers ?? []).map(l => l.id)),
    )
  }
}

/** Слои раздела «Изобаты» (шаг задаёт человек): цвет и толщина — по глубине.
 *
 *  Здесь глубин бывает сотня, и раскрашивать их «по списку» нечем: собственные
 *  цвета есть у пяти уровней телеграм-карты, остальные девяносто пять рисовались
 *  одинаковой серой волосяной линией — карта превращалась в штриховку. Цвет
 *  берётся из шкалы батиметрии (`isobathDepthColor`), поэтому линия 300 м
 *  того же тона, что дно на 300 м, и глубина читается без легенды.
 *
 *  Раз цвет и толщина — выражения по `depth`, а штрихов тут нет, весь раздел
 *  укладывается в ТРИ слоя вместо двух на глубину (на шаге 10 м было 200).
 *
 *  Белая подложка обязательна: без неё линии тонут в растре батиметрии, откуда
 *  взята сама шкала, — цвет линии там совпадает с цветом фона под ней. */
function addRampIsobathLayers(
  map: maplibregl.Map,
  key: string,
  src: string,
  depths: number[],
  bathy: LayerPalette | undefined,
) {
  const before = () => isobathBeforeId((map.getStyle().layers ?? []).map(l => l.id))

  map.addLayer(
    {
      id: isobathRampLayerId(key, 'casing'),
      type: 'line',
      source: src,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#ffffff',
        'line-width': isobathRampCasingWidthExpr() as maplibregl.ExpressionSpecification,
        'line-opacity': 0.55,
      },
    },
    before(),
  )
  map.addLayer(
    {
      id: isobathRampLayerId(key, 'line'),
      type: 'line',
      source: src,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': isobathRampColorExpr(bathy) as maplibregl.ExpressionSpecification,
        'line-width': isobathRampWidthExpr() as maplibregl.ExpressionSpecification,
        'line-opacity': 0.95,
      },
    },
    before(),
  )
  // Картинки подписей регистрируем заранее — `icon-image` выбирает из них
  // выражением. Падение одной подписи не должно уносить слой целиком.
  for (const depth of depths) {
    try {
      ensureIsobathLabelImage(map, depth, ISOBATH_LABEL_INK)
    } catch {
      /* без подписи, но с линией */
    }
  }
  map.addLayer(
    {
      id: isobathRampLayerId(key, 'label'),
      type: 'symbol',
      source: src,
      minzoom: ISOBATH_LABEL_MIN_ZOOM,
      layout: {
        'icon-image': isobathRampIconExpr() as maplibregl.DataDrivenPropertyValueSpecification<string>,
        'symbol-placement': 'line-center',
        'icon-allow-overlap': false,
        'icon-rotation-alignment': 'viewport',
        'icon-padding': 4,
      },
    },
    before(),
  )
}

/** Снять слои перечисленных глубин. Список — тот, по которому они строились:
 *  зашитая пятёрка сняла бы не всё, что создано под произвольный шаг. Снимаем
 *  обе формы — и слой-на-глубину, и тройку раздела с шагом: какая из них
 *  построена, знает только `addIsobathLayers`, а убирать надо наверняка. */
function dropIsobathLayers(map: maplibregl.Map, key: string, depths: number[]) {
  for (const part of ['label', 'line', 'casing'] as const) {
    const lyr = isobathRampLayerId(key, part)
    if (map.getLayer(lyr)) map.removeLayer(lyr)
  }
  for (const depth of depths) {
    for (const lyr of [isobathLabelLayerId(key, depth), isobathLayerId(key, depth)]) {
      if (map.getLayer(lyr)) map.removeLayer(lyr)
    }
  }
}

/** Подпись температуры фронта («14°») картинкой — по той же причине, что и у
 *  изобат (нет glyph-сервера). Своя иконка на каждый встреченный градус, кэш по
 *  hasImage. Текст — цветом линии фронта (жёлтый), плашка ТЁМНАЯ: жёлтый на белом
 *  исчезал бы, а на тёмной плашке читается и связывается с жёлтой линией. */
function ensureFrontLabelImage(map: maplibregl.Map, temp: number) {
  const id = frontLabelImageId(temp)
  if (map.hasImage(id)) return
  const ratio = 3
  const text = `${temp}°`
  const font = `600 ${9 * ratio}px "IBM Plex Mono", ui-monospace, monospace`
  const probe = document.createElement('canvas').getContext('2d')
  if (!probe) return
  probe.font = font
  const padX = 3 * ratio
  const w = Math.ceil(probe.measureText(text).width) + padX * 2
  const h = 13 * ratio
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.fillStyle = FRONT_LABEL_BG
  if (typeof ctx.roundRect === 'function') {
    ctx.beginPath()
    ctx.roundRect(0, 0, w, h, 2 * ratio)
    ctx.fill()
  } else {
    ctx.fillRect(0, 0, w, h)
  }
  ctx.font = font
  ctx.fillStyle = frontLineColor()
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, w / 2, h / 2 + 0.5 * ratio)
  map.addImage(id, ctx.getImageData(0, 0, w, h), { pixelRatio: ratio })
}

/** Подпись высоты волны («2 м») картинкой вдоль изолинии — по той же причине, что
 *  у изобат/фронтов (нет glyph-сервера). Одна иконка на уровень, кэш по hasImage;
 *  цвет текста = цвет уровня (тот же градиент, что у линии и стрелок). */
function ensureWaveLabelImage(map: maplibregl.Map, level: number, color: string) {
  const id = waveIsoLabelImageId(level)
  if (map.hasImage(id)) return
  const ratio = 3
  const text = `${waveLabelText(level)} ${t('units.m')}`
  const font = `600 ${9 * ratio}px "IBM Plex Mono", ui-monospace, monospace`
  const probe = document.createElement('canvas').getContext('2d')
  if (!probe) return
  probe.font = font
  const padX = 3 * ratio
  const w = Math.ceil(probe.measureText(text).width) + padX * 2
  const h = 13 * ratio
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.fillStyle = 'rgba(255,255,255,0.85)'
  if (typeof ctx.roundRect === 'function') {
    ctx.beginPath()
    ctx.roundRect(0, 0, w, h, 2 * ratio)
    ctx.fill()
  } else {
    ctx.fillRect(0, 0, w, h)
  }
  ctx.font = font
  ctx.fillStyle = color
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, w / 2, h / 2 + 0.5 * ratio)
  map.addImage(id, ctx.getImageData(0, 0, w, h), { pixelRatio: ratio })
}

/** Подпись градуса на изотерме («9,5°») картинкой — по той же причине, что у
 *  изобат, фронтов и волнения (glyph-сервера у карты нет). Одна иконка на
 *  градус, кэш по hasImage; текст — цветом линии, плашка БЕЛАЯ: графитовая
 *  изотерма на тёмной плашке фронтов исчезла бы. */
/** Образ подписи изолинии механизма. Тот же приём, что у изотерм: MapLibre
 *  умеет data-driven `icon-image`, но не data-driven текст, поэтому каждый
 *  уровень рисуется в canvas один раз и кладётся в атлас карты. */
function ensureUpwLabelImage(map: maplibregl.Map, level: number, color: string) {
  const id = `${UPW_LABEL_PREFIX}${level}`
  if (map.hasImage(id)) return
  const ratio = 3
  const text = upwLabelText(level, getLang())
  const font = `600 ${9 * ratio}px "IBM Plex Mono", ui-monospace, monospace`
  const probe = document.createElement('canvas').getContext('2d')
  if (!probe) return
  probe.font = font
  const padX = 3 * ratio
  const w = Math.ceil(probe.measureText(text).width) + padX * 2
  const h = 13 * ratio
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.fillStyle = 'rgba(255,255,255,0.85)'
  if (typeof ctx.roundRect === 'function') {
    ctx.beginPath()
    ctx.roundRect(0, 0, w, h, 2 * ratio)
    ctx.fill()
  } else {
    ctx.fillRect(0, 0, w, h)
  }
  ctx.font = font
  ctx.fillStyle = color
  ctx.textBaseline = 'middle'
  ctx.fillText(text, padX, h / 2)
  map.addImage(id, { width: w, height: h, data: ctx.getImageData(0, 0, w, h).data }, { pixelRatio: ratio })
}


function ensureIsothermLabelImage(map: maplibregl.Map, temp: number, color: string) {
  const id = isothermLabelImageId(temp)
  if (map.hasImage(id)) return
  const ratio = 3
  const text = isothermLabelText(temp, getLang())
  const font = `600 ${9 * ratio}px "IBM Plex Mono", ui-monospace, monospace`
  const probe = document.createElement('canvas').getContext('2d')
  if (!probe) return
  probe.font = font
  const padX = 3 * ratio
  const w = Math.ceil(probe.measureText(text).width) + padX * 2
  const h = 13 * ratio
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.fillStyle = 'rgba(255,255,255,0.85)'
  if (typeof ctx.roundRect === 'function') {
    ctx.beginPath()
    ctx.roundRect(0, 0, w, h, 2 * ratio)
    ctx.fill()
  } else {
    ctx.fillRect(0, 0, w, h)
  }
  ctx.font = font
  ctx.fillStyle = color
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, w / 2, h / 2 + 0.5 * ratio)
  map.addImage(id, ctx.getImageData(0, 0, w, h), { pixelRatio: ratio })
}

const DATA_SOURCE = 'cabinet-data'
const DATA_LAYER = 'cabinet-data-layer'

export interface MapBBox {
  lat_min: number
  lat_max: number
  lon_min: number
  lon_max: number
}

export interface MapCanvasHandle {
  /** Отрисовать основной data-слой из шаблона тайлов. */
  showLayer: (tileUrlTemplate: string) => void
  /** Перезапросить тайлы основного слоя (для прогрессивного докрашивания). */
  refreshLayer: (tileUrlTemplate: string) => void
  /** Убрать основной data-слой (карта снова пустая). */
  clearLayer: () => void
  /** Добавить/обновить тайл-оверлей по ключу. */
  addOverlay: (key: string, tileUrlTemplate: string) => void
  removeOverlay: (key: string) => void
  /** Добавить/обновить GeoJSON-оверлей (точки) по ключу.
   *  color — строка ИЛИ data-driven выражение MapLibre (напр. окраска по свойству
   *  stale). radius — число или выражение (напр. по свойству size). */
  setGeoJson: (
    key: string,
    data: GeoJSON.FeatureCollection,
    color?: string | maplibregl.ExpressionSpecification,
    radius?: number | maplibregl.ExpressionSpecification,
    opacity?: number,
  ) => void
  removeGeoJson: (key: string) => void
  /** Отрисовать землетрясения: зона воздействия (круг в километрах), её кромка
   *  и эпицентр. Три слоя на одни данные — так эти карты рисовались и для
   *  телеграма, и размер круга там значил область влияния события. */
  setQuakes: (data: GeoJSON.FeatureCollection) => void
  removeQuakes: () => void
  /** Отрисовать живой флот: symbol-слой, где форма кодирует знание о курсе
   *  (треугольник остриём по курсу / точка), а цвет — свежесть позиции. */
  setVessels: (key: string, data: GeoJSON.FeatureCollection, opacity?: number) => void
  removeVessels: (key: string) => void
  /** Отрисовать снос по течениям: смешанная коллекция линий-треков + снесённых точек.
   *  Из одного geojson-source создаются ДВА слоя (line под circle) по типу геометрии. */
  setDrift: (key: string, data: GeoJSON.FeatureCollection) => void
  removeDrift: (key: string) => void
  /** Трек одного судна: линии-сегменты операций (переход/поиск/промысел/…).
   *  Два слоя: сплошной и пунктирный для разрывов связи — `line-dasharray` в
   *  MapLibre не выражение, одним слоем их не развести. */
  setVesselTrack: (key: string, data: GeoJSON.FeatureCollection) => void
  removeVesselTrack: (key: string) => void
  /** Двадцать мест нейрокарты: кружок с номером места.
   *  Номер печатается НА карте — без него все двадцать читаются как равные, а
   *  список тем и отличается от пятна, что упорядочен. */
  setNnMarks: (key: string, data: GeoJSON.FeatureCollection) => void
  removeNnMarks: (key: string) => void
  /** Прогнозный ход судна на сутки: линия и отметки часов. */
  setVesselForecast: (key: string, data: GeoJSON.FeatureCollection) => void
  removeVesselForecast: (key: string) => void
  /** Стрелки направления течений: symbol-слой с поворотом по курсу.
   *  scale — единый размер стрелки на текущем зуме (чтобы соседние не касались). */
  setArrows: (
    key: string,
    data: GeoJSON.FeatureCollection,
    scale: number,
    colorByWave?: boolean,
  ) => void
  removeArrows: (key: string) => void
  /** Границы экономических зон: три слоя штриховки (сплошные рубежи, выведенные
   *  линии, спорные) плюс белая подложка и подписи стран. Штриховкой управляет
   *  СЛОЙ, а не свойство: `line-dasharray` в MapLibre выражений не принимает. */
  setEez: (data: GeoJSON.FeatureCollection) => void
  removeEez: () => void
  setDistricts: (data: GeoJSON.FeatureCollection) => void
  removeDistricts: () => void
  setBans: (data: GeoJSON.FeatureCollection) => void
  removeBans: () => void
  /** Тип дна (карты ГГК-1000): заливка по классу, под рубежами. */
  setSubstrate: (data: GeoJSON.FeatureCollection) => void
  removeSubstrate: () => void
  /** Навигационные предупреждения NGA: зоны, линии и точки, среди рубежей. */
  setNavwarn: (data: GeoJSON.FeatureCollection) => void
  removeNavwarn: () => void
  /** Изобаты: линии равных глубин (свойство depth) — слой на каждую глубину.
   *  Ложатся НАД растром, но ПОД стрелками/точками (см. isobathBeforeId). */
  setIsobaths: (key: string, data: GeoJSON.FeatureCollection) => void
  removeIsobaths: (key: string) => void
  /** Изотерма: линия равной температуры (белая подложка + тёмная линия), поверх
   *  всего. Обновление данных на смену градуса — через setData, без пересоздания. */
  setIsotherms: (
    key: string,
    data: GeoJSON.FeatureCollection,
    labels?: GeoJSON.FeatureCollection<GeoJSON.Point, IsoLabelProps>,
  ) => void
  removeIsotherms: (key: string) => void
  /** Изолинии механизма апвеллинга: фичи с `level`, `zero`, `color` плюс точки
   *  подписей. Ключ — сам механизм: у каждого свой источник и свои слои. */
  setUpwIso: (
    mechanism: string,
    data: GeoJSON.FeatureCollection,
    labels?: GeoJSON.FeatureCollection<GeoJSON.Point, UpwIsoLabelProps>,
  ) => void
  removeUpwIso: (mechanism: string) => void
  /** Изо-линии высоты волны: фичи с properties.color (цвет уровня) + level, плюс
   *  точки-подписи высоты («2 м») вдоль линий (icon-image по уровню). */
  setWaveContours: (
    key: string,
    data: GeoJSON.FeatureCollection,
    labels: GeoJSON.FeatureCollection<GeoJSON.Point, WaveLabelProps>,
  ) => void
  removeWaveContours: (key: string) => void
  /** Термические фронты: линия максимального градиента (маджента + подложка) +
   *  подписи T вдоль неё. Обновление на смену силы — через setData. */
  setFronts: (
    key: string,
    line: GeoJSON.Feature<GeoJSON.MultiLineString>,
    labels: GeoJSON.FeatureCollection<GeoJSON.Point, { temp: number }>,
  ) => void
  removeFronts: (key: string) => void
  /** «Траектории по HSI»: усики обратной адвекции (линии, окрашенные HSI) +
   *  кружки-origin. Сосуществует со сносом косяков (setDrift) — отдельный слой.
   *  Шкала окраски — палитра растра HSI, её карта берёт из каталога сама.
   *  Смена дня/данных — через setData. */
  setHsiDrift: (
    key: string,
    lines: GeoJSON.FeatureCollection<GeoJSON.LineString, { hsi: number }>,
    origins: GeoJSON.FeatureCollection<GeoJSON.Point, { hsi: number }>,
  ) => void
  removeHsiDrift: (key: string) => void
  /** Кометы «Траекторий по HSI»: кадр анимации (звенья с яркостью). Слой живёт
   *  отдельно от статичных усиков — их на время анимации снимает ModelsPage.
   *  Повторный вызов только заливает данные (setData), слои не пересоздаются. */
  setHsiComets: (
    key: string,
    frame: GeoJSON.FeatureCollection<GeoJSON.LineString, { hsi: number; a: number; tip: number }>,
  ) => void
  removeHsiComets: (key: string) => void
  /** Текущий bbox видимой области. */
  getBBox: () => MapBBox
  getMap: () => maplibregl.Map | null
}

interface MapCanvasProps {
  /** lng/lat клика + пиксельная точка на карте (для формы у курсора). */
  onPointClick?: (lngLat: { lng: number; lat: number; x: number; y: number }) => void
  /** Клик по существующему маркеру косяка (слой 'clusters') → редактирование. */
  onClusterClick?: (info: { id: number; lng: number; lat: number; x: number; y: number }) => void
  /** Клик по снесённой точке → её координаты (перенести в навигатор). */
  onDriftClick?: (info: {
    lng: number
    lat: number
    x: number
    y: number
    source: string
    frozen: boolean
    role: string
    ref: number
    props: Record<string, unknown>
  }) => void
  /** Клик по живому AIS-судну (слой 'vessels') → карточка у судна.
   *  props — свойства фичи (mmsi/name/flag_state/company/rfmo_type/age_h/sog/cog…). */
  onVesselClick?: (info: {
    lng: number
    lat: number
    props: Record<string, unknown>
  }) => void
  /** Наведение мыши на судно (слой 'vessels') → карточка-подсказка у судна;
   *  null — курсор ушёл с точки. Свойства фичи те же, что у onVesselClick.
   *  Координаты — ФИЧИ, а не курсора: карточка привязана к объекту (MapCallout),
   *  и от дрожания мыши её место не зависит. */
  onVesselHover?: (
    info: { lng: number; lat: number; props: Record<string, unknown> } | null,
  ) => void
  /** Наведение на маркер косяка (слой 'clusters') → читалка; null — курсор ушёл. */
  onClusterHover?: (
    info: { lng: number; lat: number; props: Record<string, unknown> } | null,
  ) => void
  /** Наведение на снесённую точку (слой 'drift') → её карточка; null — курсор ушёл. */
  onDriftHover?: (
    info: { lng: number; lat: number; props: Record<string, unknown> } | null,
  ) => void
  /** Клик по землетрясению (эпицентр ИЛИ его зона) → закреплённая карточка. */
  /** Клик по рубежу ИЭЗ → карточка «чей рубеж и на каком основании». */
  onEezClick?: (info: { lng: number; lat: number; props: Record<string, unknown> }) => void
  onEezHover?: (
    info: { lng: number; lat: number; props: Record<string, unknown> } | null,
  ) => void
  onQuakeClick?: (info: { lng: number; lat: number; props: Record<string, unknown> }) => void
  /** Наведение на землетрясение → читалка; null — курсор ушёл. */
  onQuakeHover?: (
    info: { lng: number; lat: number; props: Record<string, unknown> } | null,
  ) => void
  /** Центр/зум после завершения пана/зума — для сохранения в URL. */
  onMoveEnd?: (view: { lng: number; lat: number; zoom: number }) => void
  center?: [number, number]
  zoom?: number
}

// Слой маркеров косяков (setGeoJson('clusters', …)). Клик по нему = правка точки.
const CLUSTERS_LAYER = OVERLAY_PREFIX + 'geo-clusters-layer'
// Слой снесённых точек (setDrift('drift', …)). Клик по нему = показать координаты.
const DRIFT_POINT_LAYER = OVERLAY_PREFIX + 'drift-drift-point'
// Слой живых AIS-судов (setVessels('vessels', …)). Клик по нему = карточка судна.
const VESSELS_LAYER = OVERLAY_PREFIX + 'vessels-vessels-layer'
// Землетрясения (setQuakes). Три слоя на один источник событий: заливка зоны,
// её кромка и эпицентр. Ловить наведение и клик обязаны и зона, и эпицентр:
// зона — это и есть то, ради чего событие на карте, а кружок эпицентра мелкий.
const QUAKE_KEY = 'quakes'
const QUAKE_ZONE_LAYER = OVERLAY_PREFIX + 'quake-' + QUAKE_KEY + '-zone'
const QUAKE_EDGE_LAYER = OVERLAY_PREFIX + 'quake-' + QUAKE_KEY + '-edge'
const QUAKE_POINT_LAYER = OVERLAY_PREFIX + 'quake-' + QUAKE_KEY + '-point'
// Спрашиваем ТОЛЬКО эпицентр. Зона — это площадь в десятки километров: она
// накрывает косяки и суда, стоящие внутри неё, и на обзорном зуме занимает
// половину экрана. Карточка, выскакивающая от одного попадания курсора внутрь
// круга, мешает работать с тем, что под ним (замечено владельцем 14.08.2026).
const QUAKE_HIT_LAYERS = [QUAKE_POINT_LAYER]

export const MapCanvas = forwardRef<MapCanvasHandle, MapCanvasProps>(function MapCanvas(
  {
    onPointClick,
    onClusterClick,
    onDriftClick,
    onVesselClick,
    onVesselHover,
    onClusterHover,
    onDriftHover,
    onEezClick,
    onEezHover,
    onQuakeClick,
    onQuakeHover,
    onMoveEnd,
    center = [150, 47],
    zoom = 4,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  /** Отказы плиток подложки подряд и вывод из них. Живут здесь, а не в
   *  модуле: карта в разделе одна, а полоску рисует этот же компонент. */
  const basemapFails = useRef(0)
  const [basemapBroken, setBasemapBroken] = useState(false)
  // Род последнего отказа: надпись называет, кому чинить (см. basemapHealth).
  const [basemapCause, setBasemapCause] = useState<BasemapFailure | null>(null)
  // Шлюз «стиль разобран». Все обновления слоёв идут через него: прямая проверка
  // isStyleLoaded() роняет обновление, пока грузятся тайлы (см. styleGate.ts).
  const gateRef = useRef(createStyleGate())
  // Глубины, под которые СЕЙЧАС построены слои изобат (ключ коллекции → список).
  // Набор задаёт выбранный человеком шаг, поэтому снимать и пересобирать слои
  // приходится по факту, а не по зашитому перечню глубин.
  const isobathDepthsRef = useRef(new Map<string, number[]>())
  // Шкала глубин для изобат — из каталога слоёв, чтобы линия 300 м была того же
  // тона, что дно на 300 м, и на домене партнёра тоже. Через ref: слои строит
  // императивный обработчик, а не рендер.
  const bathyPalette = useLayerPalette('bathymetry')
  const bathyPaletteRef = useRef(bathyPalette)
  bathyPaletteRef.current = bathyPalette
  // Шкала «Траекторий по HSI» — та же, что красит растр HSI (слой `probability`).
  // Усик лежит поверх этого растра, и своя гамма делала бы одно и то же число
  // двумя разными цветами. Через ref по той же причине, что и батиметрия: слои
  // строит императивный обработчик.
  const hsiPalette = useLayerPalette('probability')
  const hsiPaletteRef = useRef(hsiPalette)
  hsiPaletteRef.current = hsiPalette
  // Экземпляр карты в состоянии (не только в ref): шкале нужен ре-рендер,
  // когда карта появилась.
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null)
  const clickCbRef = useRef(onPointClick)
  clickCbRef.current = onPointClick
  const clusterClickCbRef = useRef(onClusterClick)
  clusterClickCbRef.current = onClusterClick
  const driftClickCbRef = useRef(onDriftClick)
  driftClickCbRef.current = onDriftClick
  const vesselClickCbRef = useRef(onVesselClick)
  vesselClickCbRef.current = onVesselClick
  const vesselHoverCbRef = useRef(onVesselHover)
  vesselHoverCbRef.current = onVesselHover
  const clusterHoverCbRef = useRef(onClusterHover)
  clusterHoverCbRef.current = onClusterHover
  const driftHoverCbRef = useRef(onDriftHover)
  driftHoverCbRef.current = onDriftHover
  const eezClickCbRef = useRef(onEezClick)
  eezClickCbRef.current = onEezClick
  const eezHoverCbRef = useRef(onEezHover)
  eezHoverCbRef.current = onEezHover
  const quakeClickCbRef = useRef(onQuakeClick)
  quakeClickCbRef.current = onQuakeClick
  const quakeHoverCbRef = useRef(onQuakeHover)
  quakeHoverCbRef.current = onQuakeHover
  const moveEndCbRef = useRef(onMoveEnd)
  moveEndCbRef.current = onMoveEnd

  // --- Подсветка усика «Траекторий по HSI» под курсором ---------------------
  // Слои усиков рождаются не здесь, а в setHsiDrift, и их id зависит от ключа
  // оверлея — поэтому список целей опроса ведётся ссылкой (слой линий → его
  // ореол), а не собирается из стиля: map.getStyle() сериализует стиль целиком,
  // и на каждое движение мыши это неподъёмно.
  const hsiHoverRef = useRef({
    layers: new Map<string, string>(),
    /** Что подсвечено сейчас — чтобы не дёргать setFilter вхолостую. */
    tid: null as string | null,
    /** Заявка на опрос: движений мыши десятки в секунду, опрос идёт раз в кадр. */
    frame: 0,
    point: { x: 0, y: 0 },
  })
  const hsiHighlightOn = useHsiHighlight()
  const hsiHighlightOnRef = useRef(hsiHighlightOn)
  hsiHighlightOnRef.current = hsiHighlightOn

  const applyHsiHoverRef = useRef((_tid: string | null) => {})
  applyHsiHoverRef.current = (tid: string | null) => {
    const map = mapRef.current
    const st = hsiHoverRef.current
    if (!map || st.tid === tid) return
    st.tid = tid
    const filter = hsiHoverFilter(tid) as maplibregl.FilterSpecification
    for (const hoverLyr of st.layers.values()) {
      if (map.getLayer(hoverLyr)) map.setFilter(hoverLyr, filter)
    }
  }

  /** Усик под курсором → его tid. Опрашиваем КВАДРАТОМ, а не точкой: усик на
   *  обзорных зумах шириной 0,7 px, и попасть в него пикселем невозможно. */
  const pickHsiTidRef = useRef((): string | null => null)
  pickHsiTidRef.current = () => {
    const map = mapRef.current
    const st = hsiHoverRef.current
    if (!map) return null
    const layers = [...st.layers.keys()].filter(id => map.getLayer(id))
    if (!layers.length) return null
    const { x, y } = st.point
    const hit = map.queryRenderedFeatures(
      [
        [x - HSI_HOVER_HIT_PX, y - HSI_HOVER_HIT_PX],
        [x + HSI_HOVER_HIT_PX, y + HSI_HOVER_HIT_PX],
      ],
      { layers },
    )[0]
    const tid = hit?.properties?.tid
    return tid == null ? null : String(tid)
  }

  // Настройку выключили с открытой картой — снимаем подсветку сразу, не дожидаясь
  // движения мыши (иначе последний подсвеченный усик остался бы гореть).
  useEffect(() => {
    if (!hsiHighlightOn) applyHsiHoverRef.current(null)
  }, [hsiHighlightOn])

  /** Погасить все читалки-при-наведении: объекта под курсором больше нет.
   *
   *  Делегаты MapLibre (`map.on('mouseleave', слой, …)`) слушают ТОЛЬКО
   *  `mousemove` и `mouseout` — см. `src/ui/map.ts`, `delegates: {mousemove,
   *  mouseout}`. Значит объект может уехать из-под НЕПОДВИЖНОГО курсора, а
   *  события не будет: замер на голом MapLibre (сценарии в scratchpad) —
   *  `mouseleave` не приходит, если карту сдвинули/зумнули программно,
   *  контейнер сменил размер или слой перезалил данные через `setData`.
   *  Именно последнее и оставляло карточку вылова висеть над картой: докачка
   *  волн/изотерм/HSI перечитывает снос, `setDrift` зовёт `setData`, точка
   *  из-под курсора пропадает — а «читалка» остаётся.
   *
   *  Гасим все три: слои разные, а промах один и тот же. Повторный вызов с
   *  `null` безвреден — React на том же значении не перерисовывает. */
  const clearHoversRef = useRef(() => {})
  clearHoversRef.current = () => {
    driftHoverCbRef.current?.(null)
    vesselHoverCbRef.current?.(null)
    clusterHoverCbRef.current?.(null)
    quakeHoverCbRef.current?.(null)
    applyHsiHoverRef.current(null)
  }

  // Каталог слоёв приезжает по сети, и усики вполне могут родиться раньше него:
  // тогда они стоят цветом акцента (честное «ещё не по шкале»). Пришедшую шкалу
  // доводим до уже построенных слоёв — пересоздавать их незачем, меняется
  // только краска.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !hsiPalette) return
    const color = hsiColorExpr(hsiPalette) as maplibregl.ExpressionSpecification
    gateRef.current.run(() => {
      for (const layer of map.getStyle()?.layers ?? []) {
        const id = layer.id
        if (id.startsWith(HSI_DRIFT_PREFIX)) {
          if (id.endsWith('-line')) map.setPaintProperty(id, 'line-color', color)
          // Цвет origin ушёл в обводку: заливки у полого кружка нет.
          if (id.endsWith('-origin')) map.setPaintProperty(id, 'circle-stroke-color', color)
        } else if (id.startsWith(HSI_COMET_PREFIX) && id.endsWith('-body')) {
          // Голова кометы белая намеренно — её шкала не касается.
          map.setPaintProperty(id, 'line-color', color)
        }
      }
    })
  }, [hsiPalette])

  useEffect(() => {
    if (!containerRef.current) return
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: baseStyle(),
      // Подпись рисуем сами: встроенный блок MapLibre пишет своё имя в тот же
      // угол и лезет под знак поставщика — см. attributionControl.test.ts
      attributionControl: false,
      center,
      zoom,
      // Рамка домена: карта не уводится туда, где сервер откажет.
      maxBounds: domainMaxBounds(),
      // Без этого пиксели карты снять нельзя: WebGL очищает буфер сразу после
      // отрисовки, и в скачанном PNG/GIF оказалась бы пустота. Флаг ставится
      // только при создании карты — включить его «на время экспорта» нечем.
      preserveDrawingBuffer: true,
    })
    // Пересоздание карты (StrictMode, ремоунт) — новый шлюз: старая очередь
    // относилась к уже уничтоженной карте.
    const gate = createStyleGate()
    gateRef.current = gate
    map.on('load', () => gate.markReady())
    // Поворот отключён: компаса в интерфейсе нет, а координатная шкала рисуется
    // прямыми линиями по краям — при повороте меридианы перестали бы быть
    // вертикальными, и шкала врала бы.
    map.dragRotate.disable()
    map.touchZoomRotate.disableRotation()
    map.keyboard.disableRotation()
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
    map.on('click', e => {
      // Снесённая точка — ПЕРВЫЙ приоритет: это ответ на вопрос «где косяк
      // сейчас», за её координатами и приходят. Отметка (слой косяков) под ней
      // приглушена и отвечает за правку, поэтому проверяется следом.
      if (map.getLayer(DRIFT_POINT_LAYER)) {
        const hit = map.queryRenderedFeatures(e.point, { layers: [DRIFT_POINT_LAYER] })[0]
        if (hit && hit.geometry.type === 'Point') {
          const [lng, lat] = hit.geometry.coordinates as [number, number]
          driftClickCbRef.current?.({
            lng,
            lat,
            x: e.point.x,
            y: e.point.y,
            source: String(hit.properties?.source ?? ''),
            frozen: Boolean(hit.properties?.frozen),
            role: String(hit.properties?.role ?? 'day'),
            // id исходной точки: по отметке косяка открывается форма правки —
            // раньше этот клик ловил слой `clusters`, теперь маркер его.
            ref: Number(hit.properties?.ref),
            // Свойства целиком — карточке нужна градация улова и её объяснение.
            props: hit.properties ?? {},
          })
          return
        }
      }
      // Клик по существующему маркеру косяка → редактирование (приоритет над
      // созданием новой точки). queryRenderedFeatures опрашивает только слой
      // косяков, если он есть.
      if (map.getLayer(CLUSTERS_LAYER)) {
        const hits = map.queryRenderedFeatures(e.point, { layers: [CLUSTERS_LAYER] })
        const hit = hits[0]
        const id = hit?.properties?.id
        if (id != null) {
          const [lng, lat] =
            hit.geometry.type === 'Point'
              ? (hit.geometry.coordinates as [number, number])
              : [e.lngLat.lng, e.lngLat.lat]
          clusterClickCbRef.current?.({ id: Number(id), lng, lat, x: e.point.x, y: e.point.y })
          return
        }
      }
      // Живое AIS-судно → карточка (после правки/сноса: они приоритетнее и живут
      // только в режиме течений; судовой слой информационный и виден в любой модели).
      if (map.getLayer(VESSELS_LAYER)) {
        const hit = map.queryRenderedFeatures(e.point, { layers: [VESSELS_LAYER] })[0]
        if (hit && hit.geometry.type === 'Point') {
          const [lng, lat] = hit.geometry.coordinates as [number, number]
          vesselClickCbRef.current?.({ lng, lat, props: hit.properties ?? {} })
          return
        }
      }
      // Землетрясение — ПОСЛЕДНИМ среди объектов: у эпицентра и косяка кружки
      // одного порядка, и в спорной точке правку отметки терять нельзя.
      for (const lyr of QUAKE_HIT_LAYERS) {
        if (!map.getLayer(lyr)) continue
        const hit = map.queryRenderedFeatures(e.point, { layers: [lyr] })[0]
        if (hit && hit.geometry.type === 'Point') {
          const [lng, lat] = hit.geometry.coordinates as [number, number]
          quakeClickCbRef.current?.({ lng, lat, props: hit.properties ?? {} })
          return
        }
      }
      // Рубеж ИЭЗ — ПОСЛЕДНИМ: он линия, а не точка, и уступает всем маркерам.
      // Спрашиваем только сами линии, без белой подложки: её ширина сделала бы
      // «мёртвую зону» отсчёта в точке вчетверо шире самой линии.
      for (const dash of EEZ_DASH_GROUPS) {
        const lyr = eezDashLayerId(dash)
        if (!map.getLayer(lyr)) continue
        const hit = map.queryRenderedFeatures(e.point, { layers: [lyr] })[0]
        if (hit) {
          // Привязка — точка КУРСОРА на линии: своей «точки» у рубежа нет, а
          // его середина может лежать за краем экрана.
          eezClickCbRef.current?.({
            lng: e.lngLat.lng, lat: e.lngLat.lat, props: hit.properties ?? {},
          })
          return
        }
      }
      clickCbRef.current?.({ lng: e.lngLat.lng, lat: e.lngLat.lat, x: e.point.x, y: e.point.y })
    })
    // Курсор-указатель над кликабельными маркерами (косяки/снос/суда/события).
    for (const lyr of [CLUSTERS_LAYER, DRIFT_POINT_LAYER, VESSELS_LAYER, QUAKE_POINT_LAYER]) {
      map.on('mouseenter', lyr, () => { map.getCanvas().style.cursor = 'pointer' })
      map.on('mouseleave', lyr, () => { map.getCanvas().style.cursor = '' })
    }
    // Наведение на судно → карточка-подсказка. mousemove (а не mouseenter): при
    // переходе курсора с одного судна на соседнее внутри слоя mouseenter не
    // повторяется, и подсказка застыла бы на первом.
    map.on('mousemove', VESSELS_LAYER, e => {
      const hit = e.features?.[0]
      if (!hit || hit.geometry.type !== 'Point') return
      const [lng, lat] = hit.geometry.coordinates as [number, number]
      vesselHoverCbRef.current?.({ lng, lat, props: hit.properties ?? {} })
    })
    map.on('mouseleave', VESSELS_LAYER, () => vesselHoverCbRef.current?.(null))
    // Косяк: читалка по наведению, форма правки по клику.
    map.on('mousemove', CLUSTERS_LAYER, e => {
      const hit = e.features?.[0]
      if (!hit || hit.geometry.type !== 'Point') return
      const [lng, lat] = hit.geometry.coordinates as [number, number]
      clusterHoverCbRef.current?.({ lng, lat, props: hit.properties ?? {} })
    })
    map.on('mouseleave', CLUSTERS_LAYER, () => clusterHoverCbRef.current?.(null))
    // Снесённая точка: та же карточка, что по клику, — координаты приходят из
    // геометрии фичи (не из курсора), иначе цифры «плыли» бы вместе с мышью.
    map.on('mousemove', DRIFT_POINT_LAYER, e => {
      const hit = e.features?.[0]
      if (!hit || hit.geometry.type !== 'Point') return
      const [lng, lat] = hit.geometry.coordinates as [number, number]
      driftHoverCbRef.current?.({ lng, lat, props: hit.properties ?? {} })
    })
    map.on('mouseleave', DRIFT_POINT_LAYER, () => driftHoverCbRef.current?.(null))
    // Рубеж ИЭЗ: читалка по наведению. mousemove, а не mouseenter — при
    // переходе курсора на соседний рубеж внутри того же слоя mouseenter не
    // повторяется, и карточка застыла бы на первом.
    for (const dash of EEZ_DASH_GROUPS) {
      const lyr = eezDashLayerId(dash)
      map.on('mousemove', lyr, e => {
        const hit = e.features?.[0]
        if (!hit) return
        eezHoverCbRef.current?.({
          lng: e.lngLat.lng, lat: e.lngLat.lat, props: hit.properties ?? {},
        })
      })
      map.on('mouseleave', lyr, () => eezHoverCbRef.current?.(null))
      map.on('mouseenter', lyr, () => { map.getCanvas().style.cursor = 'pointer' })
    }
    // Землетрясение: читалка по наведению на ЭПИЦЕНТР. mousemove, а не
    // mouseenter: при переходе курсора на соседнее событие внутри того же слоя
    // mouseenter не повторяется, и карточка застыла бы на первом.
    for (const lyr of QUAKE_HIT_LAYERS) {
      map.on('mousemove', lyr, e => {
        const hit = e.features?.[0]
        if (!hit || hit.geometry.type !== 'Point') return
        const [lng, lat] = hit.geometry.coordinates as [number, number]
        quakeHoverCbRef.current?.({ lng, lat, props: hit.properties ?? {} })
      })
      map.on('mouseleave', lyr, () => quakeHoverCbRef.current?.(null))
    }
    // Подсветка усика под курсором. Слушаем карту целиком, а не слой усиков:
    // делегат слоя срабатывает только при точном попадании в геометрию, а усик
    // на обзорных зумах тоньше пикселя — опрашиваем квадратом вокруг курсора.
    //
    // Опрос идёт раз в кадр: движений мыши десятки в секунду, а
    // queryRenderedFeatures по слою в сотни усиков — не бесплатен.
    map.on('mousemove', e => {
      const st = hsiHoverRef.current
      if (!st.layers.size) return
      if (!hsiHighlightOnRef.current) {
        applyHsiHoverRef.current(null)
        return
      }
      st.point = { x: e.point.x, y: e.point.y }
      if (st.frame) return
      st.frame = requestAnimationFrame(() => {
        st.frame = 0
        applyHsiHoverRef.current(pickHsiTidRef.current())
      })
    })
    // Курсор ушёл с карты — гасим: движения мыши больше не будет, и подсветка
    // осталась бы гореть на снятом PNG и в ролике.
    map.on('mouseout', () => applyHsiHoverRef.current(null))
    // Карта поехала или сменила размер — объект ушёл из-под курсора, а мышь не
    // двигалась, и mouseleave не придёт (см. clearHoversRef). Гасим читалки сами.
    map.on('move', () => clearHoversRef.current())
    map.on('resize', () => clearHoversRef.current())
    // Кадр — в хранилище, откуда его читает список видов. Здесь, а не в
    // разделах: карта одна на все разделы, а подписчиков у кадра будет больше.
    // Холостой вызов (тот же кадр) хранилище отсеет само — перерисовки не будет.
    const publishViewBBox = () => {
      const b = map.getBounds()
      setViewBBox(canonicalBBox({
        west: b.getWest(), east: b.getEast(),
        south: b.getSouth(), north: b.getNorth(),
      }))
    }
    // ПО ГОТОВНОСТИ, а не только по движению. Свежая страница карту никуда не
    // везёт — её создают сразу в нужной точке, — поэтому одного `moveend` было
    // мало: кадра не существовало, и фильтр видов по району молча отключался,
    // отдавая полный справочник (замечено владельцем 14.08.2026: в Рижском
    // заливе предлагались сайра и камчатский краб).
    map.on('load', publishViewBBox)
    // moveend (после пана/зума/полёта) → сообщаем центр+зум наверх для URL.
    map.on('moveend', () => {
      const c = map.getCenter()
      moveEndCbRef.current?.({ lng: c.lng, lat: c.lat, zoom: map.getZoom() })
      publishViewBBox()
    })
    mapRef.current = map
    setMapInstance(map)
    // Карточки карты живут ВНЕ этого компонента (у каждой свой запрос и своя
    // страница), а привязаны к географии — им нужна проекция. Регистр модульный:
    // см. activeMap.ts.
    setActiveMap(map)
    // Смена языка кабинета меняет АДРЕС тайла подложки (у Яндекса `lang=`),
    // а карта строится один раз — учёт довозит новый адрес до неё.
    const unregisterBasemap = registerBasemapMap(map)
    // Отказ подложки: у Яндекса плохой ключ даёт 403 на КАЖДУЮ плитку, и
    // MapLibre молча оставляет фон — наши поля повисают в пустоте, а выглядит
    // это как сломанная карта. Считаем отказы источника подложки и после
    // порога говорим вслух (см. basemapHealth).
    const onError = (e: { sourceId?: string; error?: unknown }) => {
      if (e?.sourceId !== BASE_SOURCE_ID) return
      const cause = basemapFailure(e.error)
      if (!cause) return // отменённая плитка (ушла из кадра) — не отказ
      basemapFails.current += 1
      if (basemapDown(basemapFails.current)) {
        setBasemapCause(cause)
        setBasemapBroken(true)
      }
    }
    map.on('error', onError as never)
    // Первая же удачная плитка снимает обвинение: сеть моргнула — не отказ.
    const onData = (e: { sourceId?: string; tile?: unknown }) => {
      if (e?.sourceId !== BASE_SOURCE_ID || !e.tile) return
      basemapFails.current = 0
      setBasemapBroken(false)
      setBasemapCause(null)
    }
    map.on('sourcedata', onData as never)
    return () => {
      map.off('error', onError as never)
      map.off('sourcedata', onData as never)
      unregisterBasemap()
      releaseActiveMap(map)
      map.remove()
      mapRef.current = null
      setMapInstance(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const showRaster = (tileUrlTemplate: string) => {
    const map = mapRef.current
    if (!map) return
    const apply = () => {
      if (map.getLayer(DATA_LAYER)) map.removeLayer(DATA_LAYER)
      if (map.getSource(DATA_SOURCE)) map.removeSource(DATA_SOURCE)
      // maxzoom обязателен для нарезанных пирамид: за пределом сервер отдаёт
      // прозрачные тайлы, и слой «пропадает» на приближении (см. rasterZoom.ts).
      // У ленивых слоёв ключа maxzoom быть НЕ должно — иначе addSource падает.
      map.addSource(DATA_SOURCE, rasterSourceSpec(tileUrlTemplate))
      // ПОД оверлеями, но над базовой подложкой. beforeId обязателен: слой
      // пересоздаётся при каждой смене даты/глубины, и без него растр лёг бы
      // поверх стрелок и сноса — они уходили под полупрозрачный фон.
      map.addLayer(
        { id: DATA_LAYER, type: 'raster', source: DATA_SOURCE, paint: { 'raster-opacity': 0.85 } },
        firstOverlayLayerId((map.getStyle().layers ?? []).map(l => l.id)),
      )
    }
    gateRef.current.run(apply)
  }

  useImperativeHandle(ref, (): MapCanvasHandle => ({
    showLayer: showRaster,
    refreshLayer(tileUrlTemplate: string) {
      const map = mapRef.current
      if (!map) return
      const src = map.getSource(DATA_SOURCE) as maplibregl.RasterTileSource | undefined
      // setTiles на существующем source перезапрашивает тайлы без пересоздания слоя
      // (без мигания). Если слоя ещё нет — эквивалентно showLayer.
      //
      // Предел зума setTiles НЕ меняет, поэтому при смене типа слоя (пирамида ↔
      // ленивый рендер) источник пересоздаём: иначе ленивый слой унаследовал бы
      // чужой потолок и мылился на приближении, а пирамида — потеряла свой.
      const wantMax = rasterMaxZoom(tileUrlTemplate)
      if (src && (src.maxzoom ?? undefined) !== wantMax) {
        showRaster(tileUrlTemplate)
        return
      }
      if (src && typeof src.setTiles === 'function') {
        src.setTiles([tileUrlTemplate])
      } else {
        showRaster(tileUrlTemplate)
      }
    },
    clearLayer() {
      const map = mapRef.current
      if (!map) return
      // Через тот же гейт, что и показ: иначе снятие, пришедшее до разбора стиля,
      // стало бы no-op, а поставленный в очередь показ всё равно материализовался
      // бы — на карте остался бы слой, которого никто не ждёт.
      gateRef.current.run(() => {
        if (map.getLayer(DATA_LAYER)) map.removeLayer(DATA_LAYER)
        if (map.getSource(DATA_SOURCE)) map.removeSource(DATA_SOURCE)
      })
    },
    addOverlay(key: string, tileUrlTemplate: string) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + key
      const lyr = OVERLAY_PREFIX + key + '-layer'
      const apply = () => {
        if (map.getLayer(lyr)) map.removeLayer(lyr)
        if (map.getSource(src)) map.removeSource(src)
        // Ленивые оверлеи (облачность/туман) не имеют предела пирамиды →
        // rasterSourceSpec ОПУСКАЕТ ключ maxzoom. Иначе `maxzoom: undefined`
        // ронял addSource, и оверлей молча не появлялся (регрессия clouds/fog).
        map.addSource(src, rasterSourceSpec(tileUrlTemplate))
        // ПОД рубежи: оверлей переукладывается на каждой смене даты, и без
        // точки вставки лёд ложился поверх границ ИЭЗ и промрайонов.
        addUnderBoundaries(map, { id: lyr, type: 'raster', source: src, paint: { 'raster-opacity': 0.8 } })
      }
      gateRef.current.run(apply)
    },
    removeOverlay(key: string) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + key
      const lyr = OVERLAY_PREFIX + key + '-layer'
      if (map.getLayer(lyr)) map.removeLayer(lyr)
      if (map.getSource(src)) map.removeSource(src)
    },
    setGeoJson(key, data, color = earthquakeColor(), radius = 5, opacity = 0.85) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'geo-' + key
      const lyr = src + '-layer'
      const apply = () => {
        const existing = map.getSource(src) as maplibregl.GeoJSONSource | undefined
        if (existing) {
          existing.setData(data)
          clearHoversRef.current()   // объект под курсором подменился (см. clearHoversRef)
          // Прозрачность зависит от режима (снос вкл/выкл), а слой создаётся один
          // раз — без переприменения тумблер сноса не перекрасил бы отметки.
          if (map.getLayer(lyr)) {
            map.setPaintProperty(lyr, 'circle-opacity', opacity)
            map.setPaintProperty(lyr, 'circle-stroke-opacity', opacity)
          }
          return
        }
        map.addSource(src, { type: 'geojson', data })
        map.addLayer({
          id: lyr,
          type: 'circle',
          source: src,
          paint: {
            'circle-radius': radius,
            'circle-color': color,
            'circle-opacity': opacity,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#ffffff',
            // Белая обводка на полной непрозрачности держала бы маркер громким
            // даже при бледной заливке — гасим вместе с ней.
            'circle-stroke-opacity': opacity,
          },
        })
      }
      gateRef.current.run(apply)
    },
    removeGeoJson(key: string) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'geo-' + key
      const lyr = src + '-layer'
      if (map.getLayer(lyr)) map.removeLayer(lyr)
      if (map.getSource(src)) map.removeSource(src)
      clearHoversRef.current()   // слоя нет — читалке не над чем висеть
    },
    setQuakes(data) {
      const map = mapRef.current
      if (!map) return
      const zoneSrc = OVERLAY_PREFIX + 'quake-' + QUAKE_KEY + '-zone-src'
      const pointSrc = OVERLAY_PREFIX + 'quake-' + QUAKE_KEY + '-point-src'
      // Зоны считаются ОДИН раз на приход данных: круг задан в километрах и в
      // географии не меняется, а пересборка полигонов на каждое движение карты
      // была бы работой впустую.
      const zones = earthquakeZones(data)
      const apply = () => {
        const zoneExisting = map.getSource(zoneSrc) as maplibregl.GeoJSONSource | undefined
        if (zoneExisting) {
          zoneExisting.setData(zones)
          ;(map.getSource(pointSrc) as maplibregl.GeoJSONSource | undefined)?.setData(data)
          clearHoversRef.current()   // объект под курсором подменился
          return
        }
        map.addSource(zoneSrc, { type: 'geojson', data: zones })
        map.addSource(pointSrc, { type: 'geojson', data })
        const color = earthquakeColor()
        // Заливка зоны: густота от магнитуды (см. zoneOpacity). Ступенями, а не
        // плавно, — это оценка силы, а не измерение, и три ступени читаются.
        addUnderBoundaries(map, {
          id: QUAKE_ZONE_LAYER,
          type: 'fill',
          source: zoneSrc,
          paint: {
            'fill-color': color,
            'fill-opacity': [
              'case',
              ['>=', ['coalesce', ['get', 'magnitude'], 0], 6.5], zoneOpacity(7),
              ['>=', ['coalesce', ['get', 'magnitude'], 0], 5.0], zoneOpacity(5.5),
              zoneOpacity(4),
            ] as maplibregl.ExpressionSpecification,
          },
        })
        // Кромка: без неё край зоны на пёстрой карте теряется, и круг читается
        // как размытое пятно, а не как граница влияния.
        map.addLayer({
          id: QUAKE_EDGE_LAYER,
          type: 'line',
          source: zoneSrc,
          paint: { 'line-color': color, 'line-width': 1, 'line-opacity': 0.55 },
        })
        map.addLayer({
          id: QUAKE_POINT_LAYER,
          type: 'circle',
          source: pointSrc,
          paint: {
            'circle-radius': [
              'interpolate', ['linear'], ['coalesce', ['get', 'magnitude'], 4],
              4, epicenterRadius(4), 7, epicenterRadius(7),
            ] as maplibregl.ExpressionSpecification,
            'circle-color': color,
            'circle-opacity': 0.9,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#ffffff',
          },
        })
      }
      gateRef.current.run(apply)
    },
    removeQuakes() {
      const map = mapRef.current
      if (!map) return
      for (const lyr of [QUAKE_POINT_LAYER, QUAKE_EDGE_LAYER, QUAKE_ZONE_LAYER]) {
        if (map.getLayer(lyr)) map.removeLayer(lyr)
      }
      for (const src of [
        OVERLAY_PREFIX + 'quake-' + QUAKE_KEY + '-zone-src',
        OVERLAY_PREFIX + 'quake-' + QUAKE_KEY + '-point-src',
      ]) {
        if (map.getSource(src)) map.removeSource(src)
      }
      clearHoversRef.current()   // слоя нет — читалке не над чем висеть
    },
    setVessels(key, data, opacity = 0.9) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'vessels-' + key
      const lyr = src + '-layer'
      const apply = () => {
        ensureVesselImages(map)
        const existing = map.getSource(src) as maplibregl.GeoJSONSource | undefined
        if (existing) {
          existing.setData(data)
          clearHoversRef.current()   // объект под курсором подменился (см. clearHoversRef)
          if (map.getLayer(lyr)) map.setPaintProperty(lyr, 'icon-opacity', opacity)
          return
        }
        map.addSource(src, { type: 'geojson', data })
        map.addLayer({
          id: lyr,
          type: 'symbol',
          source: src,
          layout: {
            'icon-image': vesselIconImage(),
            // rotation-alignment 'map': судно развёрнуто относительно СЕВЕРА
            // карты, а не экрана — иначе поворот карты увёл бы все курсы.
            'icon-rotate': vesselIconRotate(),
            'icon-rotation-alignment': 'map',
            // Суда стоят гуще, чем помещается: без overlap MapLibre выбрасывает
            // столкнувшиеся маркеры, и на промысле пропадала бы половина флота.
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
          paint: { 'icon-opacity': opacity },
        })
      }
      gateRef.current.run(apply)
    },
    removeVessels(key: string) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'vessels-' + key
      const lyr = src + '-layer'
      if (map.getLayer(lyr)) map.removeLayer(lyr)
      if (map.getSource(src)) map.removeSource(src)
      clearHoversRef.current()   // слоя нет — читалке не над чем висеть
    },
    setDrift(key, data) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'drift-' + key
      const haloSrc = src + '-halo'
      const haloLyr = src + '-halo-fill'
      const lineLyr = src + '-line'
      const aheadLineLyr = src + '-line-ahead'
      const trackArrowLyr = src + '-track-arrow'
      const pointLyr = src + '-point'
      // Ореолы неопределённости дневных маркеров (полигоны-круги радиусом σ).
      const haloData = haloCollection(data as unknown as DriftCollection)
      const apply = () => {
        ensureTrackArrowImage(map)
        // Обновление существующего source — как в setGeoJson (без пересоздания слоёв).
        const existing = map.getSource(src) as maplibregl.GeoJSONSource | undefined
        if (existing) {
          existing.setData(data)
          const eh = map.getSource(haloSrc) as maplibregl.GeoJSONSource | undefined
          if (eh) eh.setData(haloData)
          // Точки под курсором подменились — mouseleave на это не придёт.
          clearHoversRef.current()
          return
        }
        // Ореол — ПЕРВЫМ слоем стека сноса: под треками и точками, но над цветовой
        // картой течений. Заливка полупрозрачна, обводка чуть плотнее — «область
        // вероятного положения». Растёт с днями/прогнозом, сжимается при анализе.
        map.addSource(haloSrc, { type: 'geojson', data: haloData })
        addUnderBoundaries(map, {
          id: haloLyr,
          type: 'fill',
          source: haloSrc,
          paint: {
            'fill-color': '#38bdf8',
            'fill-opacity': 0.12,
            'fill-outline-color': 'rgba(56,189,248,0.5)',
          },
        })
        map.addSource(src, { type: 'geojson', data })

        const trackWidth = [
              'case',
              ['==', ['get', 'segment'], 'context'],
              TRACK_WIDTH_CONTEXT,
              TRACK_WIDTH_ACCENT,
            ] as maplibregl.ExpressionSpecification
        const trackOpacity = [
              '*',
              ['case', ['==', ['get', 'segment'], 'context'], TRACK_CONTEXT_FADE, 1],
              [
                'interpolate',
                ['linear'],
                ['coalesce', ['get', 'age_days'], 0],
                0,
                TRACK_OPACITY_FRESH,
                30,
                TRACK_OPACITY_OLD,
              ],
            ] as maplibregl.ExpressionSpecification
        const isTrack = ['==', ['geometry-type'], 'LineString'] as maplibregl.ExpressionSpecification

        // Слои ЛИНИЙ (треки наблюдение→снос) — под точками и стрелками.
        // line-dasharray не поддерживает data-driven выражения в MapLibre, поэтому делим
        // на два слоя. Делим по ВРЕМЕНИ (segment), а не по статусу: сплошная — путь,
        // пройденный к выбранному дню, пунктир — предстоящий. Статус кодирует маркер
        // (полый = трек замёрз), иначе одно начертание значило бы два разных смысла.
        // line-opacity затухает по возрасту (легенда: «линия бледнеет = возраст»),
        // но с полом — старый трек должен оставаться находимым (см. trackStyle.ts).
        map.addLayer({
          id: lineLyr,
          type: 'line',
          source: src,
          filter: ['all', isTrack, ['!=', ['get', 'phase'], 'forecast']],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': TRACK_COLOR, // зелёный — как маркер косяка
            // Толще и ярче — отрезок между сегодня и выбранным днём; остальной
            // трек уходит в бледный контекст «откуда косяк пришёл».
            'line-width': trackWidth,
            'line-opacity': trackOpacity,
          },
        })
        // Пунктирный слой предстоящего пути (после выбранного дня).
        map.addLayer({
          id: aheadLineLyr,
          type: 'line',
          source: src,
          filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', ['get', 'phase'], 'forecast']],
          layout: { 'line-cap': 'butt', 'line-join': 'round' },
          paint: {
            'line-color': TRACK_COLOR,
            'line-width': trackWidth,
            'line-dasharray': [2, 2],
            'line-opacity': trackOpacity,
          },
        })
        // Стрелки НАПРАВЛЕНИЯ ТРЕКА: symbol-placement 'line' сам доворачивает
        // иконку по касательной, поэтому в спрайте она смотрит вправо (+x).
        // Порядок точек трека — от старой к новой, значит стрелка показывает,
        // куда косяк уносит, а не откуда пришёл.
        map.addLayer({
          id: trackArrowLyr,
          type: 'symbol',
          source: src,
          filter: isTrack,
          layout: {
            'icon-image': TRACK_ARROW_IMAGE_ID,
            'symbol-placement': 'line',
            'symbol-spacing': TRACK_ARROW_SPACING_PX,
            // без этого MapLibre выбрасывает стрелки, столкнувшиеся со
            // стрелками течений и маркерами — на длинном треке их не осталось бы
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
          paint: {
            'icon-opacity': trackOpacity,
          },
        })
        // Слой ТОЧЕК (снесённое положение). Радиус: косяк — по size_class,
        // вылов — по catch_tons. Ветвим по source, а НЕ по ['has','size_class']:
        // бэкенд шлёт ключ size_class всегда (у вылова = null), т.е. `has` был бы
        // true и для вылова → ветка catch_tons была бы мёртвой.
        map.addLayer({
          id: pointLyr,
          type: 'circle',
          source: src,
          // Точки — да, линии — нет; плюс отсев вылова с огрублёнными
          // координатами (см. driftPointFilter): маркер по сдвинутой точке
          // выглядел бы точным местом операции.
          filter: driftPointFilter(),
          // Выражения — в driftMarkerStyle.ts (там же их тесты). Три роли одной
          // точки: origin (где зарегистрировали), today (где сейчас), day (где
          // на выбранный день). Роль задаёт размер и заливку, источник — цвет.
          paint: {
            'circle-radius': driftCircleRadius(),
            'circle-color': driftCircleColor(),
            'circle-opacity': driftCircleOpacity(),
            'circle-stroke-width': driftCircleStrokeWidth(),
            'circle-stroke-color': driftCircleStrokeColor(),
          },
        })
      }
      gateRef.current.run(apply)
    },
    setArrows(key, data, scale, colorByWave = false) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'arrows-' + key
      const lyr = src + '-layer'
      const apply = () => {
        ensureArrowImage(map)
        // Оверлей «Волнение»: готовим рампу цветных образов (штиль → красный).
        if (colorByWave) ensureWaveArrowImages(map)
        const iconImage = arrowIconImage(colorByWave)
        const existing = map.getSource(src) as maplibregl.GeoJSONSource | undefined
        if (existing) {
          existing.setData(data)
          // размер одинаков у всех стрелок, но зависит от зума (на обзорных
          // сетка сгущается) — обновляем, иначе после приближения останется старый
          if (map.getLayer(lyr)) {
            map.setLayoutProperty(lyr, 'icon-size', scale)
            // тумблер «Волнение» мог переключиться — обновляем окраску стрелок
            map.setLayoutProperty(lyr, 'icon-image', iconImage)
          }
          return
        }
        map.addSource(src, { type: 'geojson', data })
        map.addLayer({
          id: lyr,
          type: 'symbol',
          source: src,
          layout: {
            'icon-image': iconImage,
            // курс «куда течёт», градусы по часовой от севера — иконка нарисована
            // остриём вверх, поэтому поворот берётся как есть
            'icon-rotate': ['get', 'bearing'],
            // 'map' — стрелка поворачивается вместе с картой, а не с экраном
            'icon-rotation-alignment': 'map',
            // единый размер: модуль скорости кодирует фон, стрелка — только курс
            'icon-size': scale,
            // сетка регулярная и стрелки заведомо не касаются, поэтому
            // раскладку MapLibre отключаем — иначе он выбросит часть стрелок
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
        })
      }
      gateRef.current.run(apply)
    },
    setEez(data) {
      const map = mapRef.current
      if (!map) return
      // Укладка ОБЩАЯ с разделом «Буи» (`map/eezLayer`): вторая её копия
      // разъехалась бы с первой при первой же правке.
      gateRef.current.run(() => applyEezLayers(map, data))
    },
    removeEez() {
      const map = mapRef.current
      if (!map) return
      removeEezLayers(map)
    },
    setDistricts(data) {
      const map = mapRef.current
      if (!map) return
      gateRef.current.run(() => applyDistrictLayers(map, data))
    },
    removeDistricts() {
      const map = mapRef.current
      if (!map) return
      removeDistrictLayers(map)
    },
    setBans(data) {
      const map = mapRef.current
      if (!map) return
      // Под линии районов, если они уже лежат: номер района читается сквозь
      // бледную заливку запрета.
      gateRef.current.run(() => applyBanLayers(map, data, DISTRICT_LAYER_IDS[0]))
    },
    removeBans() {
      const map = mapRef.current
      if (!map) return
      removeBanLayers(map)
    },
    setSubstrate(data) {
      const map = mapRef.current
      if (!map) return
      gateRef.current.run(() => applySubstrateLayers(map, data))
    },
    removeSubstrate() {
      const map = mapRef.current
      if (!map) return
      removeSubstrateLayers(map)
    },
    setNavwarn(data) {
      const map = mapRef.current
      if (!map) return
      gateRef.current.run(() => applyNavwarnLayers(map, data))
    },
    removeNavwarn() {
      const map = mapRef.current
      if (!map) return
      removeNavwarnLayers(map)
    },
    setIsobaths(key, data) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'isobath-src-' + key
      const apply = () => {
        // Глубины берём из самих данных: набор уровней задаёт шаг, выбранный
        // человеком, и зашитой пятёркой он не описывается (см. isobathDepthsOf).
        const depths = isobathDepthsOf(data)
        const existing = map.getSource(src) as maplibregl.GeoJSONSource | undefined
        if (existing) {
          existing.setData(data)
          const before = isobathDepthsRef.current.get(key) ?? []
          // Смена шага меняет НАБОР глубин, а не только геометрию: у каждого
          // слоя свой фильтр `depth == …`, поэтому старые слои надо снять и
          // построить новые. Без этого шаг 50 → 30 оставлял на карте слои
          // прежних глубин, к которым в новых данных нет ни одной линии.
          if (before.length !== depths.length || before.some((d, i) => d !== depths[i])) {
            dropIsobathLayers(map, key, before)
            addIsobathLayers(map, key, src, depths, bathyPaletteRef.current)
            isobathDepthsRef.current.set(key, depths)
          }
          return
        }
        map.addSource(src, { type: 'geojson', data })
        addIsobathLayers(map, key, src, depths, bathyPaletteRef.current)
        isobathDepthsRef.current.set(key, depths)
      }
      gateRef.current.run(apply)
    },
    removeIsobaths(key: string) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'isobath-src-' + key
      dropIsobathLayers(map, key, isobathDepthsRef.current.get(key) ?? [])
      isobathDepthsRef.current.delete(key)
      if (map.getSource(src)) map.removeSource(src)
    },
    setIsotherms(key, data, labels) {
      const map = mapRef.current
      if (!map) return
      const src = isothermSourceId(key)
      const lblSrc = isothermLabelSourceId(key)
      const empty: GeoJSON.FeatureCollection<GeoJSON.Point, IsoLabelProps> = {
        type: 'FeatureCollection',
        features: [],
      }
      const lbl = labels ?? empty
      const apply = () => {
        // Картинки подписей — до setData, иначе data-driven icon-image не найдёт
        // образ. Кэш по hasImage.
        for (const f of lbl.features) ensureIsothermLabelImage(map, f.properties.temp, f.properties.color)

        const existing = map.getSource(src) as maplibregl.GeoJSONSource | undefined
        if (existing) {
          // Смена градуса → только новые данные. Слои не трогаем: пересоздание
          // мигает и стоит дороже, а нам нужна мгновенная перерисовка. Подписи
          // обновляются ВМЕСТЕ с линиями: разъехавшись, они называют чужой
          // градус — правдоподобно и не проверяемо.
          existing.setData(data)
          ;(map.getSource(lblSrc) as maplibregl.GeoJSONSource | undefined)?.setData(lbl)
          return
        }
        map.addSource(src, { type: 'geojson', data })
        map.addSource(lblSrc, { type: 'geojson', data: lbl })
        // Подложка ПОД линией — иначе белый ореол лёг бы поверх графита.
        map.addLayer({
          id: isothermLayerId(key, 'casing'),
          type: 'line',
          source: src,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': ISOTHERM_CASING_COLOR,
            'line-width': isothermCasingWidthExpr() as maplibregl.ExpressionSpecification,
            'line-opacity': ISOTHERM_CASING_OPACITY,
          },
        })
        map.addLayer({
          id: isothermLayerId(key, 'line'),
          type: 'line',
          source: src,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            // Цвет — из свойства фичи: одним кодом рисуются и глубинное
            // семейство, и поверхностная линия другого цвета. Старые вызовы без
            // свойства падают на умолчание гаммы.
            'line-color': [
              'coalesce',
              ['get', 'color'],
              isothermLineColor(),
            ] as unknown as maplibregl.ExpressionSpecification,
            'line-width': isothermLineWidthExpr() as maplibregl.ExpressionSpecification,
          },
        })
        map.addLayer({
          id: isothermLayerId(key, 'labels'),
          type: 'symbol',
          source: lblSrc,
          // Ниже этого зума ярлыков нет: линий в семействе до шести десятков.
          minzoom: ISO_LABEL_MIN_ZOOM,
          layout: {
            'icon-image': ['concat', ISO_LABEL_PREFIX, ['to-string', ['get', 'temp']]] as unknown as string,
            // Налезающие MapLibre отбрасывает сам → плотность подписей под зум.
            'icon-allow-overlap': false,
            'icon-rotation-alignment': 'viewport',
            'icon-padding': 6,
          },
        })
        raiseArrowsToTop(map, key)
      }
      gateRef.current.run(apply)
    },
    removeIsotherms(key: string) {
      const map = mapRef.current
      if (!map) return
      for (const part of ['labels', 'line', 'casing'] as const) {
        const lyr = isothermLayerId(key, part)
        if (map.getLayer(lyr)) map.removeLayer(lyr)
      }
      for (const src of [isothermSourceId(key), isothermLabelSourceId(key)]) {
        if (map.getSource(src)) map.removeSource(src)
      }
    },
    setUpwIso(mechanism, data, labels) {
      const map = mapRef.current
      if (!map) return
      const src = upwIsoSourceId(mechanism)
      const lblSrc = upwIsoLabelSourceId(mechanism)
      const empty: GeoJSON.FeatureCollection<GeoJSON.Point, UpwIsoLabelProps> = {
        type: 'FeatureCollection', features: [],
      }
      const lbl = labels ?? empty
      const apply = () => {
        // Образы подписей — ДО setData, иначе data-driven `icon-image` не найдёт
        // картинку и подпись просто не появится (молча).
        for (const f of lbl.features) ensureUpwLabelImage(map, f.properties.level, f.properties.color)

        const existing = map.getSource(src) as maplibregl.GeoJSONSource | undefined
        if (existing) {
          existing.setData(data)
          ;(map.getSource(lblSrc) as maplibregl.GeoJSONSource | undefined)?.setData(lbl)
          return
        }
        map.addSource(src, { type: 'geojson', data })
        map.addSource(lblSrc, { type: 'geojson', data: lbl })
        // Белая подложка ПОД линией: приём читаемости поверх чужого растра.
        map.addLayer({
          id: upwIsoLayerId(mechanism, 'casing'),
          type: 'line', source: src,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': UPW_CASING_COLOR,
            'line-opacity': UPW_CASING_OPACITY,
            'line-width': upwWidthExpr(3.8) as maplibregl.ExpressionSpecification,
          },
        })
        map.addLayer({
          id: upwIsoLayerId(mechanism, 'solid'),
          type: 'line', source: src,
          filter: ['all', ['!', ['get', 'zero']], ['>=', ['get', 'level'], 0]] as never,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': ['get', 'color'] as unknown as maplibregl.ExpressionSpecification,
            'line-width': upwWidthExpr(1.6) as maplibregl.ExpressionSpecification,
          },
        })
        // Опускание — ШТРИХОМ: цвет занят семейством, а `line-dasharray` в
        // MapLibre не data-driven, поэтому это отдельный слой с фильтром.
        map.addLayer({
          id: upwIsoLayerId(mechanism, 'dashed'),
          type: 'line', source: src,
          filter: ['all', ['!', ['get', 'zero']], ['<', ['get', 'level'], 0]] as never,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': ['get', 'color'] as unknown as maplibregl.ExpressionSpecification,
            'line-width': upwWidthExpr(1.6) as maplibregl.ExpressionSpecification,
            'line-dasharray': [...UPW_DASH] as never,
          },
        })
        map.addLayer({
          id: upwIsoLayerId(mechanism, 'zero'),
          type: 'line', source: src,
          filter: ['get', 'zero'] as never,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': ['get', 'color'] as unknown as maplibregl.ExpressionSpecification,
            'line-width': upwWidthExpr(UPW_ZERO_WIDTH) as maplibregl.ExpressionSpecification,
          },
        })
        map.addLayer({
          id: upwIsoLayerId(mechanism, 'label'),
          type: 'symbol', source: lblSrc,
          minzoom: 4,
          layout: {
            'icon-image': ['concat', UPW_LABEL_PREFIX, ['to-string', ['get', 'level']]] as unknown as string,
            'icon-allow-overlap': false,
            'icon-rotation-alignment': 'viewport',
            'icon-padding': 6,
          },
        })
      }
      gateRef.current.run(apply)
    },
    removeUpwIso(mechanism: string) {
      const map = mapRef.current
      if (!map) return
      for (const part of ['label', 'zero', 'dashed', 'solid', 'casing'] as const) {
        const lyr = upwIsoLayerId(mechanism, part)
        if (map.getLayer(lyr)) map.removeLayer(lyr)
      }
      for (const src of [upwIsoSourceId(mechanism), upwIsoLabelSourceId(mechanism)]) {
        if (map.getSource(src)) map.removeSource(src)
      }
    },
    setWaveContours(key, data, labels) {
      const map = mapRef.current
      if (!map) return
      const src = waveIsoSourceId(key)
      const lblSrc = waveIsoLabelSourceId(key)
      const apply = () => {
        // Картинки подписей — на каждый встреченный уровень (до setData, иначе
        // data-driven icon-image не найдёт образ). Кэш по hasImage.
        for (const f of labels.features) ensureWaveLabelImage(map, f.properties.level, f.properties.color)

        const existing = map.getSource(src) as maplibregl.GeoJSONSource | undefined
        if (existing) {
          existing.setData(data)
          ;(map.getSource(lblSrc) as maplibregl.GeoJSONSource | undefined)?.setData(labels)
          return
        }
        map.addSource(src, { type: 'geojson', data })
        map.addSource(lblSrc, { type: 'geojson', data: labels })
        // Белая подложка — как у изотерм: ink-линии низкой волны иначе тонут на
        // «медленной» (чёрной) части шкалы течений.
        map.addLayer({
          id: waveIsoLayerId(key, 'casing'),
          type: 'line',
          source: src,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': ISOTHERM_CASING_COLOR,
            'line-width': isothermCasingWidthExpr() as maplibregl.ExpressionSpecification,
            'line-opacity': ISOTHERM_CASING_OPACITY,
          },
        })
        map.addLayer({
          id: waveIsoLayerId(key, 'line'),
          type: 'line',
          source: src,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            // Цвет уровня — из свойства фичи (градиент ink→красный по высоте волны).
            'line-color': ['get', 'color'] as unknown as maplibregl.ExpressionSpecification,
            'line-width': isothermLineWidthExpr() as maplibregl.ExpressionSpecification,
          },
        })
        map.addLayer({
          id: waveIsoLayerId(key, 'labels'),
          type: 'symbol',
          source: lblSrc,
          layout: {
            // data-driven: картинка по уровню точки (образы добавлены выше).
            'icon-image': ['concat', WAVE_ISO_LABEL_PREFIX, ['to-string', ['get', 'level']]] as unknown as string,
            // Налезающие MapLibre отбрасывает сам → плотность подписей под зум.
            'icon-allow-overlap': false,
            'icon-rotation-alignment': 'viewport',
            'icon-padding': 6,
          },
        })
        raiseArrowsToTop(map, key)
      }
      gateRef.current.run(apply)
    },
    removeWaveContours(key: string) {
      const map = mapRef.current
      if (!map) return
      for (const part of ['labels', 'line', 'casing'] as const) {
        const lyr = waveIsoLayerId(key, part)
        if (map.getLayer(lyr)) map.removeLayer(lyr)
      }
      for (const src of [waveIsoSourceId(key), waveIsoLabelSourceId(key)]) {
        if (map.getSource(src)) map.removeSource(src)
      }
    },
    setFronts(key, line, labels) {
      const map = mapRef.current
      if (!map) return
      const lineSrc = frontSourceId(key, 'line')
      const labelSrc = frontSourceId(key, 'labels')
      const apply = () => {
        // Картинки подписей — на КАЖДЫЙ встреченный градус (набор меняется вместе
        // с силой). Готовим до setData, иначе data-driven icon-image не найдёт образ.
        for (const f of labels.features) ensureFrontLabelImage(map, f.properties.temp)

        const existingLine = map.getSource(lineSrc) as maplibregl.GeoJSONSource | undefined
        if (existingLine) {
          existingLine.setData(line)
          ;(map.getSource(labelSrc) as maplibregl.GeoJSONSource | undefined)?.setData(labels)
          return
        }
        map.addSource(lineSrc, { type: 'geojson', data: line })
        map.addSource(labelSrc, { type: 'geojson', data: labels })
        map.addLayer({
          id: frontLayerId(key, 'casing'),
          type: 'line',
          source: lineSrc,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': FRONT_CASING_COLOR,
            'line-width': frontCasingWidthExpr() as maplibregl.ExpressionSpecification,
            'line-opacity': FRONT_CASING_OPACITY,
          },
        })
        map.addLayer({
          id: frontLayerId(key, 'line'),
          type: 'line',
          source: lineSrc,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': frontLineColor(),
            'line-width': frontLineWidthExpr() as maplibregl.ExpressionSpecification,
          },
        })
        map.addLayer({
          id: frontLayerId(key, 'labels'),
          type: 'symbol',
          source: labelSrc,
          layout: {
            // data-driven: картинка по градусу точки (образы добавлены выше).
            'icon-image': ['concat', 'front-lbl-', ['to-string', ['get', 'temp']]] as unknown as string,
            // Налезающие MapLibre отбрасывает сам → плотность подписей под зум.
            'icon-allow-overlap': false,
            'icon-rotation-alignment': 'viewport',
            'icon-padding': 6,
          },
        })
        raiseArrowsToTop(map, key)
      }
      gateRef.current.run(apply)
    },
    removeFronts(key: string) {
      const map = mapRef.current
      if (!map) return
      for (const part of ['labels', 'line', 'casing'] as const) {
        const lyr = frontLayerId(key, part)
        if (map.getLayer(lyr)) map.removeLayer(lyr)
      }
      for (const which of ['line', 'labels'] as const) {
        const src = frontSourceId(key, which)
        if (map.getSource(src)) map.removeSource(src)
      }
    },
    setHsiDrift(key, lines, origins) {
      const map = mapRef.current
      if (!map) return
      const lineSrc = hsiDriftSourceId(key, 'line')
      const originSrc = hsiDriftSourceId(key, 'origin')
      const lineLyr = hsiDriftLayerId(key, 'line')
      const originLyr = hsiDriftLayerId(key, 'origin')
      const arrowLyr = hsiDriftLayerId(key, 'arrow')
      const hoverLyr = hsiDriftLayerId(key, 'hover')
      const color = hsiColorExpr(hsiPaletteRef.current) as maplibregl.ExpressionSpecification
      const apply = () => {
        const existing = map.getSource(lineSrc) as maplibregl.GeoJSONSource | undefined
        if (existing) {
          // Ползунок дня меняет только данные (без пересоздания слоёв → без
          // мигания). Шкала могла приехать позже слоёв — подкрашиваем оба.
          existing.setData(lines)
          ;(map.getSource(originSrc) as maplibregl.GeoJSONSource | undefined)?.setData(origins)
          if (map.getLayer(lineLyr)) map.setPaintProperty(lineLyr, 'line-color', color)
          // Цвет ушёл в обводку: заливка у полого кружка отсутствует.
          if (map.getLayer(originLyr)) {
            map.setPaintProperty(originLyr, 'circle-stroke-color', color)
          }
          raiseArrowsToTop(map, key)
          return
        }
        map.addSource(lineSrc, { type: 'geojson', data: lines })
        map.addSource(originSrc, { type: 'geojson', data: origins })
        ensureHsiArrowImage(map)
        const specs: Record<HsiDriftPart, maplibregl.LayerSpecification> = {
          // Ореол подсветки при наведении. Родится пустым (фильтр не пропускает
          // ничего) и наполняется одним усиком под курсором — см. applyHsiHover.
          hover: {
            id: hoverLyr,
            type: 'line',
            source: lineSrc,
            filter: hsiHoverFilter(null) as maplibregl.FilterSpecification,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: hsiHoverPaint() as maplibregl.LineLayerSpecification['paint'],
          },
          // Усики — над растром течений, окрашены HSI шкалой самого поля HSI.
          line: {
            id: lineLyr,
            type: 'line',
            source: lineSrc,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-color': color,
              'line-width': hsiLineWidthExpr() as maplibregl.ExpressionSpecification,
              'line-opacity': 0.85,
            },
          },
          // Шевроны направления вдоль усика: показывают, КУДА течёт вода (порядок
          // вершин origin → «сейчас»). Без них усик с залитой точкой на старом
          // конце читался как стрелка, направленная против течения.
          arrow: {
            id: arrowLyr,
            type: 'symbol',
            source: lineSrc,
            layout: hsiArrowLayout() as maplibregl.SymbolLayerSpecification['layout'],
          },
          // Кружки-origin (массив ненулевых HSI-точек 7 дн назад) — поверх усиков.
          // ПОЛЫЕ: это хвост пути, а не голова (см. hsiOriginPaint).
          origin: {
            id: originLyr,
            type: 'circle',
            source: originSrc,
            paint: hsiOriginPaint(color) as maplibregl.CircleLayerSpecification['paint'],
          },
        }
        // Порядок добавления = порядок отрисовки снизу вверх, и он написан в
        // hsiDrift.ts (там же он и проверяется тестом): ореол обязан лечь ПОД
        // линией, иначе белая подсветка закрасит сам цвет по шкале HSI.
        for (const part of hsiDriftLayerOrder()) map.addLayer(specs[part])
        // Слой линий — цель опроса под курсором, ореол — его подсветка.
        hsiHoverRef.current.layers.set(lineLyr, hoverLyr)
        // Стрелки течений держим поверх усиков (как над изотермами/фронтами).
        raiseArrowsToTop(map, key)
      }
      gateRef.current.run(apply)
    },
    removeHsiDrift(key: string) {
      const map = mapRef.current
      if (!map) return
      // Шеврон-слой и ореол подсветки живут на ИСТОЧНИКЕ линий, поэтому
      // снимаются вместе с ними (свой источник у них не заводится) — но снять
      // их надо ДО источника.
      hsiHoverRef.current.layers.delete(hsiDriftLayerId(key, 'line'))
      for (const part of ['origin', 'arrow', 'line', 'hover'] as const) {
        const lyr = hsiDriftLayerId(key, part)
        if (map.getLayer(lyr)) map.removeLayer(lyr)
      }
      for (const part of ['origin', 'line'] as const) {
        const src = hsiDriftSourceId(key, part)
        if (map.getSource(src)) map.removeSource(src)
      }
    },
    setHsiComets(key, frame) {
      const map = mapRef.current
      if (!map) return
      // Кадр анимации, в отличие от остальных слоёв, В ОЧЕРЕДЬ ШЛЮЗА НЕ КЛАДЁМ:
      // пока стиль грузится, каждый кадр (~30/с) добавил бы в очередь замыкание
      // с целым GeoJSON на сотни КБ, а к моменту слива все они устареют — карта
      // прокрутила бы задом наперёд секунды мёртвых кадров. Кадр дешевле
      // выбросить: следующий придёт через 32 мс.
      if (!gateRef.current.ready) return
      const src = hsiCometSourceId(key)
      const bodyLyr = hsiCometLayerId(key, 'body')
      const tipLyr = hsiCometLayerId(key, 'tip')
      const existing = map.getSource(src) as maplibregl.GeoJSONSource | undefined
      if (existing) {
        // Кадр анимации: только данные. Пересоздание слоёв на каждом кадре
        // дало бы мигание и мусор в стиле. Подкраски здесь тоже нет: шкалу,
        // приехавшую позже слоёв, доводит отдельный эффект ниже, а
        // setPaintProperty безусловно дёргает полный проход по стилю — платить
        // этим каждый кадр не за что.
        existing.setData(frame)
        return
      }
      // Шкала цвета считается ровно там, где рождаются слои: hsiColorExpr()
      // аллоцирует выражение целиком, и на каждом кадре это лишнее.
      const color = hsiColorExpr(hsiPaletteRef.current) as maplibregl.ExpressionSpecification
      map.addSource(src, { type: 'geojson', data: frame })
      map.addLayer({
        id: bodyLyr,
        type: 'line',
        source: src,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': color,
          'line-width': hsiCometWidthExpr() as maplibregl.ExpressionSpecification,
          'line-opacity': ['get', 'a'] as maplibregl.ExpressionSpecification,
        },
      })
      // Кончик — белым поверх тела: голова читается без ореола и свечения.
      map.addLayer({
        id: tipLyr,
        type: 'line',
        source: src,
        filter: TIP_FILTER as maplibregl.FilterSpecification,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#ffffff',
          'line-width': hsiCometWidthExpr() as maplibregl.ExpressionSpecification,
          'line-opacity': ['*', ['get', 'a'], 0.55] as maplibregl.ExpressionSpecification,
        },
      })
      raiseArrowsToTop(map, key)
    },
    removeHsiComets(key: string) {
      const map = mapRef.current
      if (!map) return
      // Снос идёт через тот же шлюз, что и добавление: пара «добавить/убрать»
      // обязана исполняться в одном порядке с одной стороны шлюза. Синхронный
      // снос до готовности стиля искал бы слои, которых ещё нет, и тихо ничего
      // не делал — а всё, что шлюз выпустит потом, осталось бы на карте
      // замороженным кадром.
      gateRef.current.run(() => {
        for (const part of ['tip', 'body'] as const) {
          const lyr = hsiCometLayerId(key, part)
          if (map.getLayer(lyr)) map.removeLayer(lyr)
        }
        const src = hsiCometSourceId(key)
        if (map.getSource(src)) map.removeSource(src)
      })
    },
    removeArrows(key: string) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'arrows-' + key
      const lyr = src + '-layer'
      if (map.getLayer(lyr)) map.removeLayer(lyr)
      if (map.getSource(src)) map.removeSource(src)
    },
    setVesselTrack(key: string, data: GeoJSON.FeatureCollection) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'vtrack-' + key
      const solidLyr = src + '-solid'
      const gapLyr = src + '-gap'
      const apply = () => {
        const existing = map.getSource(src) as maplibregl.GeoJSONSource | undefined
        if (existing) {
          existing.setData(data)
          return
        }
        map.addSource(src, { type: 'geojson', data })
        // Разрыв связи — пунктиром и ПОД сплошными сегментами: молчание не
        // должно спорить с тем, что реально измерено.
        map.addLayer({
          id: gapLyr,
          type: 'line',
          source: src,
          filter: operationFilter('gap') as maplibregl.FilterSpecification,
          layout: { 'line-cap': 'butt', 'line-join': 'round' },
          paint: {
            'line-color': operationColor('no_data'),
            'line-width': 1.6,
            'line-dasharray': GAP_DASHARRAY,
            'line-opacity': 0.7,
          },
        })
        map.addLayer({
          id: solidLyr,
          type: 'line',
          source: src,
          filter: operationFilter('solid') as maplibregl.FilterSpecification,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': operationColorExpr() as maplibregl.ExpressionSpecification,
            // Толще линии сноса: трек судна — то, что человек искал, и он не
            // должен теряться среди отрезков дрейфа.
            'line-width': 2.6,
            'line-opacity': operationOpacityExpr() as maplibregl.ExpressionSpecification,
          },
        })
      }
      gateRef.current.run(apply)
    },
    removeVesselTrack(key: string) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'vtrack-' + key
      for (const suffix of ['-solid', '-gap']) {
        const lyr = src + suffix
        if (map.getLayer(lyr)) map.removeLayer(lyr)
      }
      if (map.getSource(src)) map.removeSource(src)
    },
    setNnMarks(key: string, data: GeoJSON.FeatureCollection) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'nnmarks-' + key
      const apply = () => {
        ensureNnRankImages(map)
        const existing = map.getSource(src) as maplibregl.GeoJSONSource | undefined
        if (existing) {
          existing.setData(data)
          return
        }
        map.addSource(src, { type: 'geojson', data })
        map.addLayer({
          id: src + '-dot',
          type: 'circle',
          source: src,
          paint: markCirclePaint() as maplibregl.CircleLayerSpecification['paint'],
        })
        map.addLayer({
          id: src + '-label',
          type: 'symbol',
          source: src,
          minzoom: MARK_LABEL_MIN_ZOOM,
          layout: markLabelLayout() as maplibregl.SymbolLayerSpecification['layout'],
        })
      }
      gateRef.current.run(apply)
    },
    removeNnMarks(key: string) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'nnmarks-' + key
      for (const suffix of ['-dot', '-label']) {
        const lyr = src + suffix
        if (map.getLayer(lyr)) map.removeLayer(lyr)
      }
      if (map.getSource(src)) map.removeSource(src)
    },
    setVesselForecast(key: string, data: GeoJSON.FeatureCollection) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'vfc-' + key
      const apply = () => {
        const existing = map.getSource(src) as maplibregl.GeoJSONSource | undefined
        if (existing) {
          existing.setData(data)
          return
        }
        map.addSource(src, { type: 'geojson', data })
        // Два слоя вместо одного: `line-dasharray` не бывает управляемым
        // данными, а начертание здесь — не украшение, а сообщение о доверии.
        for (const confidence of ['soft', 'firm'] as const) {
          map.addLayer({
            id: src + '-' + confidence,
            type: 'line',
            source: src,
            filter: forecastConfidenceFilter(confidence) as maplibregl.FilterSpecification,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: forecastLinePaint(confidence) as maplibregl.LineLayerSpecification['paint'],
          })
        }
        // Отметки часов — точками поверх линии: по ним читают, ГДЕ судно
        // окажется к сроку, а не только куда идёт.
        map.addLayer({
          id: src + '-marks',
          type: 'circle',
          source: src,
          filter: ['==', ['geometry-type'], 'Point'] as maplibregl.FilterSpecification,
          paint: forecastMarkPaint() as maplibregl.CircleLayerSpecification['paint'],
        })
      }
      gateRef.current.run(apply)
    },
    removeVesselForecast(key: string) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'vfc-' + key
      for (const suffix of ['-soft', '-firm', '-marks']) {
        const lyr = src + suffix
        if (map.getLayer(lyr)) map.removeLayer(lyr)
      }
      if (map.getSource(src)) map.removeSource(src)
    },
    removeDrift(key: string) {
      const map = mapRef.current
      if (!map) return
      const src = OVERLAY_PREFIX + 'drift-' + key
      for (const suffix of ['-line', '-line-ahead', '-track-arrow', '-point', '-halo-fill']) {
        const lyr = src + suffix
        if (map.getLayer(lyr)) map.removeLayer(lyr)
      }
      if (map.getSource(src + '-halo')) map.removeSource(src + '-halo')
      if (map.getSource(src)) map.removeSource(src)
      clearHoversRef.current()   // слоя нет — читалке не над чем висеть
    },
    getBBox(): MapBBox {
      const map = mapRef.current
      if (!map) return { lat_min: 0, lat_max: 0, lon_min: 0, lon_max: 0 }
      const b = map.getBounds()
      // Через canonicalBBox, а не напрямую: у видов за антимеридианом MapLibre
      // отдаёт запад за −180 (Берингово море ≈ −189), и такой bbox бэкенд
      // отвергал с HTTP 422, а SQL-фильтр по долготе не давал ни одной строки.
      return canonicalBBox({
        west: b.getWest(),
        east: b.getEast(),
        south: b.getSouth(),
        north: b.getNorth(),
      })
    },
    getMap: () => mapRef.current,
  }))

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" />
      {/* Подпись подложки — требование условий поставщика: у Яндекса знак
          обязан стоять в углу карты и вести на Яндекс Карты, у Carto
          обязана быть видна атрибуция OSM и CARTO. */}
      <BasemapAttribution corner="br" />
      {basemapBroken && (
        <div className="pointer-events-none absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-md border border-buoy/40 bg-abyss/90 px-3 py-1.5 text-xs text-buoy shadow-console">
          {(({ key, vars }) => t(key, vars))(basemapFailureMsg(basemapCause))}
        </div>
      )}
      {/* Координатная шкала — на всех картах сервиса, поэтому живёт здесь,
          а не в страницах. */}
      <MapGraticule map={mapInstance} />
      {/* Читалка координат под курсором — по той же причине здесь, и на карту
          подписывается сама: события курсора идут на каждый пиксель, и подними
          мы их до страницы, «Модели» перерисовывались бы целиком по шестьдесят
          раз в секунду. */}
      <CoordReadout map={mapInstance} />
    </div>
  )
})
